import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Upload, 
  Image as ImageIcon, 
  Sparkles, 
  Calculator, 
  Package, 
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  Save,
  Download,
  Home,
  Ruler,
  Wind,
  AlertTriangle,
  CheckCircle,
  Info,
  FileText,
  Zap,
  Shield,
  FileDown,
  Clock,
  Hammer,
  Layers,
  MapPin
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { useToast } from '../hooks/use-toast';
import { usePricing } from '../hooks/use-pricing';
import { useClients } from '../hooks/use-clients';
import { useEstimates } from '../hooks/use-estimates';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/use-auth';
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';
import { apiRequest } from '../lib/queryClient';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

interface RoofSection {
  name: string;
  type: string;
  area: number;
  pitch: number;
  pitchRatio: string;
  /** Polygon points as percentages (0-100) of image width/height; traces actual roof edges */
  polygon?: Array<{ x: number; y: number }>;
  bounds?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  visibleInImages?: number[];
  /** Google Solar API: facet orientation (0=N, 90=E) */
  azimuthDegrees?: number;
  azimuthLabel?: string;
  sunshineQuantiles?: number[];
  centerLat?: number;
  centerLng?: number;
  planeHeightMeters?: number;
}

interface ConditionDetails {
  overallStatus: string;
  urgency: string;
  detectedIssues: string[];
  recommendations: string[];
  aiObservations: string[];
}

/** Line segment as percentages (0-100) for hip/ridge/valley/eave/rake edges */
interface StructuralLineSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  label?: string;
}

interface StructuralLines {
  hipLines?: StructuralLineSegment[];
  ridgeLines?: StructuralLineSegment[];
  valleyLines?: StructuralLineSegment[];
  eaveLines?: StructuralLineSegment[];
  rakeLines?: StructuralLineSegment[];
}

interface RoofAnalysis {
  roofType: string;
  pitch: number;
  pitchRatio: string;
  squareFootage: number;
  squares: number;
  ridgeLength: number;
  eaveLength: number;
  rakeLength?: number;
  hipLength?: number;
  valleyLength: number;
  stepFlashing?: number;
  apronFlashing?: number;
  existingLayers: number;
  primaryImageIndex?: number;
  features: {
    chimneys: number;
    skylights: number;
    vents: number;
    dormers: number;
    valleys: number;
    ridges: number;
    hips: number;
  };
  roofSections: RoofSection[];
  /** Hip, ridge, valley, eave, rake line segments (percent 0-100) for diagram */
  structuralLines?: StructuralLines;
  condition: string;
  conditionDetails: ConditionDetails;
  complexity: string;
  confidence: number;
  notes: string;
  /** When set to 'google_solar', report came from address/aerial API (no photo overlay). */
  source?: 'google_solar' | string;
  buildingFootprintSqFt?: number;
  imageryDate?: string | null;
  imageryQuality?: string | null;
  /** Optional Static Maps sky view for Google Solar reports (data URL). */
  satelliteImageDataUrl?: string | null;
  /** Populated for address-based Google Solar reports */
  solarPotentialSummary?: {
    maxArrayPanelsCount?: number | null;
    maxArrayAreaMeters2?: number | null;
    panelCapacityWatts?: number | null;
    wholeRoofSunshineQuantiles?: number[] | null;
  };
  measurementProvenance?: {
    provider: string;
    apiMethod: string;
    buildingResourceName?: string | null;
    centerLat?: number;
    centerLng?: number;
    imageryProcessedDate?: string | null;
    postalCode?: string | null;
    administrativeArea?: string | null;
    regionCode?: string | null;
  };
  /** Legal / ordering-grade disclaimer for PDF (Google Solar path) */
  orderingGradeDisclaimer?: string;
}

interface MaterialBreakdown {
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
}

interface PricingTier {
  name: string;
  multiplier: number;
  materials: MaterialBreakdown[];
  labor: number;
  total: number;
}

export default function AIRoofMeasurementPage() {
  const { toast } = useToast();
  const { services, materials, isLoading: pricingLoading } = usePricing();
  const { clients } = useClients();
  const { createEstimateMutation } = useEstimates();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<RoofAnalysis | null>(null);
  const [activeTab, setActiveTab] = useState("analysis");
  const [selectedTier, setSelectedTier] = useState<'economy' | 'standard' | 'premium'>('standard');
  const [pricingTiers, setPricingTiers] = useState<Record<string, PricingTier>>({});
  const WASTE_OPTIONS = [5, 10, 12, 15, 17, 20, 25] as const;
  const [wastePercent, setWastePercent] = useState<number>(10);
  const [regionalLaborRate, setRegionalLaborRate] = useState(50);
  const [isEstimateDialogOpen, setIsEstimateDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [estimateClientSearch, setEstimateClientSearch] = useState('');
  const [diagramViewMode, setDiagramViewMode] = useState<"grid" | "list">("grid");
  const manualDiagramRef = useRef<HTMLDivElement>(null);
  const [isManualMode, setIsManualMode] = useState(false);
  const [manualToolMode, setManualToolMode] = useState<'polygon' | 'line' | 'calibrate'>('polygon');
  const [manualPolygonName, setManualPolygonName] = useState('Plane 1');
  const [manualCurrentPoints, setManualCurrentPoints] = useState<Array<{ x: number; y: number }>>([]);
  const [manualPolygons, setManualPolygons] = useState<Array<{ name: string; points: Array<{ x: number; y: number }> }>>([]);
  const [manualLineType, setManualLineType] = useState<'hip' | 'ridge' | 'valley' | 'eave' | 'rake'>('hip');
  const [manualLineP1, setManualLineP1] = useState<{ x: number; y: number } | null>(null);
  const [manualLines, setManualLines] = useState<Array<{ type: 'hip' | 'ridge' | 'valley' | 'eave' | 'rake'; p1: { x: number; y: number }; p2: { x: number; y: number } }>>([]);
  const [manualCalibP1, setManualCalibP1] = useState<{ x: number; y: number } | null>(null);
  const [manualCalibP2, setManualCalibP2] = useState<{ x: number; y: number } | null>(null);
  const [manualCalibLengthFt, setManualCalibLengthFt] = useState<number>(0);
  const [manualOverrides, setManualOverrides] = useState<{
    squareFootage?: number;
    pitch?: number;
    ridgeLength?: number;
    eaveLength?: number;
    rakeLength?: number;
    hipLength?: number;
    valleyLength?: number;
    chimneys?: number;
    skylights?: number;
    vents?: number;
    dormers?: number;
    valleys?: number;
    ridges?: number;
    hips?: number;
  }>({});
  const [addressInput, setAddressInput] = useState('');
  const [isFetchingByAddress, setIsFetchingByAddress] = useState(false);
  const [reportAddress, setReportAddress] = useState<string>('');
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  /** Both "Save as PDF" and "Save Report as PDF" use this — same dialog + same `generatePDFReport`. */
  const openRoofPdfDialog = () => setPdfDialogOpen(true);
  const [pdfClientName, setPdfClientName] = useState('');
  const [pdfClientSearch, setPdfClientSearch] = useState('');
  const [isGeneratingRoofDescription] = useState(false);
  // Any old overage checkout return params are no longer used.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('overage_paid') === '1' || params.get('session_id')) {
      setLocation('/ai-roof-measurement');
    }
  }, [setLocation]);

  const clampPct = (n: number) => Math.max(0, Math.min(100, n));

  const confidenceLabel = (n: unknown): 'High' | 'Moderate' | 'Low' => {
    const v = typeof n === 'number' ? n : Number(n);
    if (!Number.isFinite(v)) return 'Moderate';
    if (v >= 80) return 'High';
    if (v >= 60) return 'Moderate';
    return 'Low';
  };

  const imageryLabel = (q: unknown): string => {
    const v = typeof q === 'string' ? q.trim().toUpperCase() : '';
    if (!v) return '—';
    if (v === 'HIGH') return 'High';
    if (v === 'MEDIUM') return 'Medium';
    if (v === 'LOW') return 'Low';
    return v;
  };

  const getClickPct = (e: React.MouseEvent) => {
    const el = manualDiagramRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    return { x: clampPct(x), y: clampPct(y) };
  };

  const polygonAreaPct2 = (pts: Array<{ x: number; y: number }>) => {
    if (pts.length < 3) return 0;
    let sum = 0;
    for (let i = 0; i < pts.length; i++) {
      const j = (i + 1) % pts.length;
      sum += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
    }
    return Math.abs(sum) / 2;
  };

  const distPct = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const feetPerPct = (manualCalibP1 && manualCalibP2 && manualCalibLengthFt > 0)
    ? (manualCalibLengthFt / Math.max(0.0001, distPct(manualCalibP1, manualCalibP2)))
    : null;

  const manualTotalSqFt = feetPerPct
    ? manualPolygons.reduce((sum, p) => sum + polygonAreaPct2(p.points) * feetPerPct * feetPerPct, 0)
    : null;

  const manualLineLengthFt = (l: { p1: { x: number; y: number }; p2: { x: number; y: number } }) =>
    feetPerPct ? distPct(l.p1, l.p2) * feetPerPct : null;

  // Local pricing defaults (fallback if material list doesn't have roofing materials)
  const localRoofingPrices = {
    shingles: { economy: 80, standard: 120, premium: 180 }, // per square
    underlayment: { economy: 15, standard: 25, premium: 40 }, // per square
    flashing: { economy: 2.5, standard: 4, premium: 6 }, // per linear ft
    vents: { economy: 25, standard: 45, premium: 75 }, // each
    ridgeCaps: { economy: 1.5, standard: 2.5, premium: 4 }, // per linear ft
    nails: { economy: 0.02, standard: 0.03, premium: 0.04 }, // per square
  };

  // Compress image to reduce payload size (Vercel limit: 4.5MB total)
  const compressImage = (file: File, maxSizeKB: number = 800): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions (max 1920px on longest side)
          const maxDimension = 1920;
          if (width > height) {
            if (width > maxDimension) {
              height = (height * maxDimension) / width;
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width = (width * maxDimension) / height;
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to create canvas context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to JPEG with quality compression
          let quality = 0.85;
          let dataUrl = canvas.toDataURL('image/jpeg', quality);
          
          // If still too large, reduce quality further
          while (dataUrl.length > maxSizeKB * 1024 && quality > 0.3) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
          }
          
          resolve(dataUrl);
        };
        img.onerror = () => reject(new Error(`Failed to load image: ${file.name}`));
        img.src = event.target?.result as string;
      };
      reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      return;
    }

    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      toast({
        title: "Invalid file type",
        description: "Please upload image files (JPG, PNG, etc.)",
        variant: "destructive"
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Check total file size before compression (warn if very large)
    const totalSizeMB = imageFiles.reduce((sum, file) => sum + file.size, 0) / (1024 * 1024);
    if (totalSizeMB > 50) {
      toast({
        title: "Large files detected",
        description: "Compressing images... This may take a moment.",
      });
    }

    // Compress all images
    const compressPromises = imageFiles.map(file => compressImage(file));
    
    Promise.all(compressPromises)
      .then((compressedImages) => {
        setUploadedImages(prev => [...prev, ...compressedImages]);
        toast({
          title: `${imageFiles.length} image(s) uploaded and compressed`,
          description: `Total: ${uploadedImages.length + imageFiles.length} image(s). Ready for analysis.`
        });
      })
      .catch((error) => {
        toast({
          title: "Error processing files",
          description: error.message,
          variant: "destructive"
        });
      });

    // Reset input to allow selecting again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    
    if (files.length === 0) {
      toast({
        title: "Invalid file type",
        description: "Please drop image files (JPG, PNG, etc.)",
        variant: "destructive"
      });
      return;
    }

    // Check total file size before compression
    const totalSizeMB = files.reduce((sum, file) => sum + file.size, 0) / (1024 * 1024);
    if (totalSizeMB > 50) {
      toast({
        title: "Large files detected",
        description: "Compressing images... This may take a moment.",
      });
    }

    // Compress all dropped images
    const compressPromises = files.map(file => compressImage(file));
    
    Promise.all(compressPromises)
      .then((compressedImages) => {
        setUploadedImages(prev => [...prev, ...compressedImages]);
        toast({
          title: `${files.length} image(s) uploaded and compressed`,
          description: `Total: ${uploadedImages.length + files.length} image(s). Ready for analysis.`
        });
      })
      .catch((error) => {
        toast({
          title: "Error processing files",
          description: error.message,
          variant: "destructive"
        });
      });
  };

  const handleUploadClick = (e: React.MouseEvent) => {
    // Only trigger if clicking on the upload area itself, not on buttons or images
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button') || target.tagName === 'IMG' || target.closest('img')) {
      return; // Don't trigger file input if clicking on a button or image
    }
    e.preventDefault();
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const analyzeRoof = async () => {
    if (uploadedImages.length === 0) {
      toast({
        title: "No images",
        description: "Please upload at least one roof image first",
        variant: "destructive"
      });
      return;
    }

    // Prevent duplicate calls
    if (isAnalyzing) {
      toast({
        title: "Analysis in progress",
        description: "Please wait for the current analysis to complete",
        variant: "default"
      });
      return;
    }

    setIsAnalyzing(true);
    setActiveTab("analysis");

    try {
      // Prepare all images for API
      const images = uploadedImages.map(img => {
        const base64Data = img.split(',')[1] || img;
        const imageType = img.split(';')[0].split(':')[1] || 'image/jpeg';
        return {
          imageBase64: base64Data,
          imageType
        };
      });

      const response = await apiRequest(
        'POST',
        '/api/analyze-roof',
        {
          images: images // Send array of images
        }
      );

      const json = await response.json();
      const { aiUsageThisMonth, ...roofAnalysis } = json;
      setAnalysis(roofAnalysis as RoofAnalysis);
      setManualOverrides({});
      setReportAddress('');
      
      // Calculate pricing after analysis
      calculatePricing(roofAnalysis as RoofAnalysis, wastePercent, regionalLaborRate);
      
      if (typeof aiUsageThisMonth === 'number') {
        queryClient.setQueryData(['/api/protected/subscription/status'], (prev: { plan?: { aiUsageLimit?: number | null }; usage?: { currentAiUsage?: number; maxAiUsage?: number | null; aiUsageRemaining?: number | string } } | undefined) => {
          const limit = prev?.plan?.aiUsageLimit ?? 10;
          const remaining = limit ? Math.max(0, limit - aiUsageThisMonth) : 'unlimited';
          return prev ? { ...prev, usage: { ...prev.usage, currentAiUsage: aiUsageThisMonth, maxAiUsage: limit || null, aiUsageRemaining: remaining } } : { plan: { planName: 'pro', aiUsageLimit: 10 }, usage: { currentAiUsage: aiUsageThisMonth, maxAiUsage: 10, aiUsageRemaining: remaining } };
        });
      }
      await queryClient.refetchQueries({ queryKey: ['/api/protected/subscription/status'] });
      toast({
        title: "Analysis complete",
        description: `Analyzed ${uploadedImages.length} image(s) — Confidence: ${confidenceLabel(roofAnalysis.confidence)} — ${roofAnalysis.squareFootage} sq ft detected`
      });
    } catch (error: any) {
      console.error('Analysis error:', error);
      const msg = error?.message || '';
      if (msg.startsWith('402:')) {
        toast({
          title: "Analysis failed",
          description: "Roof reports are included with your plan. Please try again.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Analysis failed",
        description: error.message || "Could not analyze roof images",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const fetchReportByAddress = async () => {
    const address = addressInput.trim();
    if (!address) {
      toast({
        title: "Enter address",
        description: "Type a property address to get a roof report",
        variant: "destructive"
      });
      return;
    }
    if (isFetchingByAddress) return;
    setIsFetchingByAddress(true);
    setActiveTab("analysis");
    try {
      const response = await apiRequest('POST', '/api/protected/roof-by-address', { address });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        if (response.status === 404) {
          toast({
            title: "Address not in coverage",
            description: err.message || "This address isn't in Google Solar coverage. Try photo-based analysis or another address.",
            variant: "destructive"
          });
          return;
        }
        if (response.status === 503) {
          toast({
            title: "Roof by address not configured",
            description: err.message || "Google Solar API key is not set. See setup guide.",
            variant: "destructive"
          });
          return;
        }
        throw new Error(err.message || `Request failed: ${response.status}`);
      }
      const json = await response.json();
      const { aiUsageThisMonth, ...roofAnalysis } = json;
      setAnalysis(roofAnalysis as RoofAnalysis);
      setManualOverrides({});
      setUploadedImages([]);
      setReportAddress(address);
      calculatePricing(roofAnalysis as RoofAnalysis, wastePercent, regionalLaborRate);
      if (typeof aiUsageThisMonth === 'number') {
        queryClient.setQueryData(['/api/protected/subscription/status'], (prev: { plan?: { aiUsageLimit?: number | null }; usage?: { currentAiUsage?: number; maxAiUsage?: number | null; aiUsageRemaining?: number | string } } | undefined) => {
          const limit = prev?.plan?.aiUsageLimit ?? 10;
          const remaining = limit ? Math.max(0, limit - aiUsageThisMonth) : 'unlimited';
          return prev ? { ...prev, usage: { ...prev.usage, currentAiUsage: aiUsageThisMonth, maxAiUsage: limit || null, aiUsageRemaining: remaining } } : { plan: { planName: 'pro', aiUsageLimit: 10 }, usage: { currentAiUsage: aiUsageThisMonth, maxAiUsage: 10, aiUsageRemaining: remaining } };
        });
      }
      await queryClient.refetchQueries({ queryKey: ['/api/protected/subscription/status'] });
      toast({
        title: "Roof report ready",
        description: `${roofAnalysis.squareFootage} sq ft — ${roofAnalysis.roofSections?.length ?? 0} segments. You can send to estimate.`
      });
    } catch (error: any) {
      console.error('Roof-by-address error:', error);
      const msg = error?.message || '';
      if (msg.startsWith('402:')) {
        toast({
          title: "Could not get roof report",
          description: "Roof reports are included with your plan. Please try again.",
          variant: "destructive",
        });
        return;
      }
      let description = msg || "Check address or try again later.";
      try {
        const match = /^\d+:\s*(\{.*\})$/.exec(msg);
        if (match) {
          const body = JSON.parse(match[1]);
          if (body?.message) description = body.message;
        }
      } catch (_) { /* use default */ }
      toast({
        title: "Could not get roof report",
        description,
        variant: "destructive"
      });
    } finally {
      setIsFetchingByAddress(false);
    }
  };

  const calculatePricing = (roofData: RoofAnalysis, wastePct: number, laborRate?: number) => {
    const squares = roofData.squares;
    const pitch = roofData.pitch;
    const complexity = roofData.complexity;
    const features = roofData.features ?? defaultFeatures;

    // Pitch multiplier (steeper roofs cost more)
    const pitchMultiplier = pitch > 30 ? 1.3 : pitch > 20 ? 1.15 : pitch > 10 ? 1.05 : 1.0;
    
    // Complexity multiplier
    const complexityMultiplier = complexity === 'complex' ? 1.4 : complexity === 'moderate' ? 1.2 : 1.0;

    // Waste: add wastePct% to material cost so full price includes waste
    const wasteMultiplier = 1 + wastePct / 100;

    // Get roofing materials from material list or use local prices
    const roofingMaterials = materials.filter(m => 
      m.category === 'roof' || m.category === 'roofing' || m.name.toLowerCase().includes('shingle')
    );

    const tiers: Record<string, PricingTier> = {};

    ['economy', 'standard', 'premium'].forEach((tier) => {
      const tierKey = tier as 'economy' | 'standard' | 'premium';
      const materialsList: MaterialBreakdown[] = [];

      // Shingles
      const shingleMaterial = roofingMaterials.find(m => 
        m.name.toLowerCase().includes('shingle') || m.name.toLowerCase().includes('asphalt')
      );
      const shinglePrice = shingleMaterial 
        ? shingleMaterial.unitPrice * (tier === 'economy' ? 0.8 : tier === 'premium' ? 1.5 : 1.0)
        : localRoofingPrices.shingles[tierKey];
      materialsList.push({
        name: 'Asphalt Shingles',
        quantity: squares,
        unit: 'squares',
        unitPrice: shinglePrice,
        total: squares * shinglePrice
      });

      // Underlayment
      const underlaymentMaterial = roofingMaterials.find(m => 
        m.name.toLowerCase().includes('underlayment') || m.name.toLowerCase().includes('felt')
      );
      const underlaymentPrice = underlaymentMaterial
        ? underlaymentMaterial.unitPrice * (tier === 'economy' ? 0.8 : tier === 'premium' ? 1.5 : 1.0)
        : localRoofingPrices.underlayment[tierKey];
      materialsList.push({
        name: 'Underlayment',
        quantity: squares,
        unit: 'squares',
        unitPrice: underlaymentPrice,
        total: squares * underlaymentPrice
      });

      // Flashing (linear feet)
      const linearFeet = Math.sqrt(squares * 100) * 4; // Approximate perimeter
      const flashingMaterial = roofingMaterials.find(m => 
        m.name.toLowerCase().includes('flashing') || m.name.toLowerCase().includes('metal')
      );
      const flashingPrice = flashingMaterial
        ? flashingMaterial.unitPrice * (tier === 'economy' ? 0.8 : tier === 'premium' ? 1.5 : 1.0)
        : localRoofingPrices.flashing[tierKey];
      materialsList.push({
        name: 'Flashing',
        quantity: linearFeet,
        unit: 'linear ft',
        unitPrice: flashingPrice,
        total: linearFeet * flashingPrice
      });

      // Ridge Caps
      const ridgeFeet = features.ridges * 20; // Approximate
      const ridgePrice = localRoofingPrices.ridgeCaps[tierKey];
      materialsList.push({
        name: 'Ridge Caps',
        quantity: ridgeFeet,
        unit: 'linear ft',
        unitPrice: ridgePrice,
        total: ridgeFeet * ridgePrice
      });

      // Vents
      if (features.vents > 0) {
        const ventPrice = localRoofingPrices.vents[tierKey];
        materialsList.push({
          name: 'Roof Vents',
          quantity: features.vents,
          unit: 'each',
          unitPrice: ventPrice,
          total: features.vents * ventPrice
        });
      }

      // Nails
      const nailPrice = localRoofingPrices.nails[tierKey];
      materialsList.push({
        name: 'Roofing Nails',
        quantity: squares,
        unit: 'squares',
        unitPrice: nailPrice,
        total: squares * nailPrice
      });

      // Calculate material total, then apply waste + pitch + complexity (full price includes waste)
      const materialTotal = materialsList.reduce((sum, m) => sum + m.total, 0);
      const adjustedMaterialTotal = materialTotal * wasteMultiplier * pitchMultiplier * complexityMultiplier;

    // Labor calculation (hours based on squares and complexity)
      const baseHours = squares * 2; // 2 hours per square base
      const complexityHours = complexity === 'complex' ? baseHours * 1.5 : complexity === 'moderate' ? baseHours * 1.2 : baseHours;
      const laborHours = complexityHours * pitchMultiplier;
      const effectiveLaborRate = typeof laborRate === 'number' && Number.isFinite(laborRate) && laborRate > 0
        ? laborRate
        : regionalLaborRate;
      const laborCost = laborHours * effectiveLaborRate;

      tiers[tier] = {
        name: tier.charAt(0).toUpperCase() + tier.slice(1),
        multiplier: tier === 'economy' ? 0.8 : tier === 'premium' ? 1.5 : 1.0,
        materials: materialsList,
        labor: laborCost,
        total: adjustedMaterialTotal + laborCost
      };
    });

    setPricingTiers(tiers);
    // Do not switch tab — let the user stay in Analysis while editing measurements; estimate updates in place
  };

  const defaultFeatures = {
    chimneys: 0,
    skylights: 0,
    vents: 0,
    dormers: 0,
    valleys: 0,
    ridges: 0,
    hips: 0,
  };

  /** Build a concise roof report summary for the AI description API (same style as estimate line descriptions). */
  const buildRoofSummaryForAI = (a: RoofAnalysis): string => {
    const feat = a.features ?? defaultFeatures;
    const parts: string[] = [
      `Total roof area: ${a.squareFootage?.toLocaleString() ?? 0} sq ft (${a.squares?.toFixed(1) ?? 0} squares).`,
      `Roof type: ${a.roofType}. Pitch: ${a.pitchRatio} (${a.pitch}°).`,
      `Complexity: ${a.complexity}. ${a.roofSections?.length ?? 0} facets/sections.`,
      `Existing layers to remove: ${a.existingLayers ?? 1}.`,
    ];
    const linear: string[] = [];
    if (a.ridgeLength) linear.push(`ridge ${a.ridgeLength} ft`);
    if (a.eaveLength) linear.push(`eave ${a.eaveLength} ft`);
    if (a.rakeLength) linear.push(`rake ${a.rakeLength} ft`);
    if (a.hipLength) linear.push(`hip ${a.hipLength} ft`);
    if (a.valleyLength) linear.push(`valley ${a.valleyLength} ft`);
    if (linear.length) parts.push(`Linear measurements: ${linear.join(', ')}.`);
    const fList: string[] = [];
    if (feat.chimneys) fList.push(`${feat.chimneys} chimney(s)`);
    if (feat.skylights) fList.push(`${feat.skylights} skylight(s)`);
    if (feat.vents) fList.push(`${feat.vents} vent(s)`);
    if (feat.dormers) fList.push(`${feat.dormers} dormer(s)`);
    if (feat.valleys) fList.push(`${feat.valleys} valley(s)`);
    if (feat.ridges) fList.push(`${feat.ridges} ridge(s)`);
    if (feat.hips) fList.push(`${feat.hips} hip(s)`);
    if (fList.length) parts.push(`Features: ${fList.join(', ')}.`);
    if (a.condition) parts.push(`Condition: ${a.condition}.`);
    if (a.conditionDetails?.overallStatus) parts.push(`Overall: ${a.conditionDetails.overallStatus}.`);
    if (a.conditionDetails?.urgency) parts.push(`Urgency: ${a.conditionDetails.urgency}.`);
    if (a.notes) parts.push(`Notes: ${a.notes}`);
    return parts.join(' ');
  };

  /** Fallback when AI description API fails — installation steps and value, same style as AI. */
  const getFallbackRoofDescription = (a: RoofAnalysis, tierData: PricingTier): string => {
    const sq = a.squares?.toFixed(1) ?? '0';
    const pkg = tierData.name === 'Economy' ? '3-tab asphalt shingles' : tierData.name === 'Standard' ? 'architectural shingles' : 'premium roofing materials';
    return `- Complete roof replacement for ${a.squareFootage?.toLocaleString() ?? 0} sq ft (${sq} squares) using ${pkg}
- Protect landscaping and property with tarps and set up a safe work area
- Remove existing roofing (${a.existingLayers ?? 1} layer(s)) and dispose of all materials responsibly
- Inspect roof deck and repair or replace any damaged sections as needed
- Install underlayment and ice & water shield at eaves, valleys, and critical areas
- Install new shingles with proper alignment, fastening, and manufacturer specifications
- Install flashing at valleys, ridges, eaves, and around chimneys, vents, and skylights
- Install ridge cap and drip edge for a finished, weathertight roof
- Clean up all job site debris and leave the property tidy
- Final walk-through to verify quality and your satisfaction`;
  };

  /** Remove lines we never want in client-facing estimates: "Address ...", "Note: Based on X squares... AI confidence", etc. */
  const sanitizeRoofDescription = (text: string): string => {
    if (!text || typeof text !== 'string') return text;
    const lines = text.split(/\r?\n/).filter((line) => {
      const t = line.trim();
      if (!t) return true;
      if (/^\-?\s*Address\s*\.{0,3}\s*$/i.test(t)) return false;
      if (/^\-?\s*Address\s*\.{2,}\s*$/i.test(t)) return false;
      if (/Note:\s*Based on.*AI confidence/i.test(t)) return false;
      if (/Based on \d+\.?\d*\s*squares.*complexity.*AI confidence/i.test(t)) return false;
      return true;
    });
    return lines.join('\n').trim();
  };

  const getEffectiveAnalysis = (): RoofAnalysis | null => {
    if (!analysis) return null;
    const features = analysis.features ?? defaultFeatures;
    const sqft = typeof manualOverrides.squareFootage === 'number' ? manualOverrides.squareFootage : analysis.squareFootage;
    const squares = sqft ? sqft / 100 : analysis.squares;

    return {
      ...analysis,
      squareFootage: typeof sqft === 'number' ? sqft : analysis.squareFootage,
      squares: typeof squares === 'number' ? squares : analysis.squares,
      pitch: typeof manualOverrides.pitch === 'number' ? manualOverrides.pitch : analysis.pitch,
      ridgeLength: typeof manualOverrides.ridgeLength === 'number' ? manualOverrides.ridgeLength : analysis.ridgeLength,
      eaveLength: typeof manualOverrides.eaveLength === 'number' ? manualOverrides.eaveLength : analysis.eaveLength,
      rakeLength: typeof manualOverrides.rakeLength === 'number' ? manualOverrides.rakeLength : (analysis.rakeLength || 0),
      hipLength: typeof manualOverrides.hipLength === 'number' ? manualOverrides.hipLength : (analysis.hipLength || 0),
      valleyLength: typeof manualOverrides.valleyLength === 'number' ? manualOverrides.valleyLength : analysis.valleyLength,
      stepFlashing: analysis.stepFlashing || 0,
      apronFlashing: analysis.apronFlashing || 0,
      features: {
        ...features,
        chimneys: typeof manualOverrides.chimneys === 'number' ? manualOverrides.chimneys : (features.chimneys ?? 0),
        skylights: typeof manualOverrides.skylights === 'number' ? manualOverrides.skylights : (features.skylights ?? 0),
        vents: typeof manualOverrides.vents === 'number' ? manualOverrides.vents : (features.vents ?? 0),
        dormers: typeof manualOverrides.dormers === 'number' ? manualOverrides.dormers : (features.dormers ?? 0),
        valleys: typeof manualOverrides.valleys === 'number' ? manualOverrides.valleys : (features.valleys ?? 0),
        ridges: typeof manualOverrides.ridges === 'number' ? manualOverrides.ridges : (features.ridges ?? 0),
        hips: typeof manualOverrides.hips === 'number' ? manualOverrides.hips : (features.hips ?? 0),
      }
    };
  };

  const saveToEstimates = () => {
    if (!analysis || !pricingTiers[selectedTier]) {
      toast({
        title: "No data to save",
        description: "Please complete analysis first",
        variant: "destructive"
      });
      return;
    }
    setIsEstimateDialogOpen(true);
  };

  const createEstimate = async () => {
    if (!selectedClientId || !analysis || !pricingTiers[selectedTier]) {
      toast({
        title: "Missing information",
        description: "Please select a client",
        variant: "destructive"
      });
      return;
    }

    const tierData = pricingTiers[selectedTier];
    const today = new Date();
    const expiryDate = new Date(today);
    expiryDate.setDate(expiryDate.getDate() + 30);

    // Generate estimate number
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 900) + 100;
    const estimateNumber = `EST-${year}${month}-${random}`;

    const effective = getEffectiveAnalysis() || analysis;
    // Single "Roof" line item with AI-generated description (same style as other estimate descriptions)
    // Start with a safe fallback description so estimate creation never depends on AI.
    let roofLineDescription: string = getFallbackRoofDescription(effective, tierData);
    try {
      const res = await apiRequest('POST', '/api/protected/ai/generate-description', {
        serviceType: 'roof',
        serviceName: 'Roof',
        quantity: effective.squares,
        unit: 'squares',
        userDescription: buildRoofSummaryForAI(effective),
      });
      const data = await res.json();
      if (data?.description) {
        roofLineDescription = sanitizeRoofDescription(data.description);
      } else {
        roofLineDescription = sanitizeRoofDescription(roofLineDescription);
      }
    } catch (err) {
      console.error('AI description generation failed, using fallback:', err);
      roofLineDescription = sanitizeRoofDescription(roofLineDescription);
    }

    const toNum = (v: any, fallback = 0) =>
      (typeof v === 'number' && Number.isFinite(v))
        ? v
        : (Number.isFinite(Number(v)) ? Number(v) : fallback);

    let ridge = toNum(effective.ridgeLength);
    let eave = toNum(effective.eaveLength);
    let valley = toNum(effective.valleyLength);
    const sq = toNum(effective.squareFootage);
    if (sq > 0) {
      if (ridge === 0) ridge = Math.round(Math.sqrt(sq));
      if (eave === 0) eave = Math.round(4 * Math.sqrt(sq));
      if (valley === 0 && (effective.roofSections?.length ?? 0) > 2) valley = Math.round(Math.sqrt(sq) * 0.5);
    }
    const rake = toNum(effective.rakeLength);
    const linearParts: string[] = [];
    if (eave > 0) linearParts.push(`Eave: ${eave} ft`);
    if (ridge > 0) linearParts.push(`Ridge: ${ridge} ft`);
    if (rake > 0) linearParts.push(`Rake: ${rake} ft`);
    linearParts.push(`Valley: ${valley} ft`);
    const measurementsLine = linearParts.length > 0 ? `\n\nKey measurements: ${linearParts.join(', ')}.` : '';
    const finalDescription = roofLineDescription + measurementsLine;

    const items = [
      {
        serviceType: 'roof' as const,
        description: finalDescription,
        quantity: '1',
        unit: 'project',
        unitPrice: tierData.total.toFixed(2),
        amount: tierData.total.toFixed(2),
        notes: linearParts.length > 0 ? linearParts.join(' | ') : '',
      },
    ];

    const safeSq = typeof analysis.squareFootage === 'number' && Number.isFinite(analysis.squareFootage)
      ? analysis.squareFootage
      : sq || 0;
    const safeSquares = typeof analysis.squares === 'number' && Number.isFinite(analysis.squares)
      ? analysis.squares
      : (safeSq / 100);

    const estimateData = {
      clientId: selectedClientId,
      estimateNumber,
      issueDate: today.toISOString(),
      expiryDate: expiryDate.toISOString(),
      status: 'draft',
      description: `Roof replacement based on AI measurement report. ${safeSq.toLocaleString()} sq ft (${safeSquares.toFixed(1)} squares), ${analysis.complexity} complexity. See line item for full scope.`,
      notes: '', // Do not expose internal AI confidence to clients or contractor
      subtotal: tierData.total.toFixed(2),
      tax: '0.00',
      discount: '0.00',
      total: tierData.total.toFixed(2),
      items,
    };

    console.log('[Roof Estimate] Creating with single line item:', { itemsCount: items.length, descriptionLength: items[0].description.length });
    try {
      const result = await createEstimateMutation.mutateAsync(estimateData);
      if (!result?.estimateId && !result?.id) {
        throw new Error('Server did not return estimate ID. The estimate may not have been saved.');
      }
      setIsEstimateDialogOpen(false);
      setSelectedClientId(null);
      await queryClient.refetchQueries({ queryKey: ['/api/protected/estimates'] });
      toast({
        title: "Estimate created",
        description: "The roof estimate has been created successfully."
      });
      setLocation('/estimates');
    } catch (error: any) {
      toast({
        title: "Error creating estimate",
        description: error.message || "Failed to create estimate",
        variant: "destructive"
      });
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-green-600 bg-green-100';
    if (confidence >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 80) return <CheckCircle2 className="h-4 w-4" />;
    return <AlertCircle className="h-4 w-4" />;
  };

  /** Draw simplified roof plan diagram from sections + structural lines (no photo background). */
  const drawRoofPlanDiagram = (
    sections: RoofSection[],
    structuralLinesData: StructuralLines | undefined,
    canvasWidth: number,
    canvasHeight: number
  ): string | null => {
    if (typeof document === 'undefined') return null;
    if (!sections || sections.length === 0) return null;

    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Background
    ctx.fillStyle = '#f9fafb';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    const toX = (x: number) => (Number(x) / 100) * canvasWidth;
    const toY = (y: number) => (Number(y) / 100) * canvasHeight;

    // Ensure every section has a polygon. If none are provided (e.g. Google Solar report),
    // synthesize simple rectangles in a grid layout just for visualization.
    const anyPolygon = sections.some(s => Array.isArray(s.polygon) && s.polygon.length >= 3);
    const sectionsWithPoly: RoofSection[] = anyPolygon
      ? sections.map((s) => {
          if (s.polygon && s.polygon.length >= 3) return s;
          return {
            ...s,
            polygon: [
              { x: 10, y: 10 },
              { x: 30, y: 10 },
              { x: 30, y: 30 },
              { x: 10, y: 30 },
            ],
          };
        })
      : (() => {
          const n = sections.length;
          const cols = Math.ceil(Math.sqrt(n || 1));
          const rows = Math.ceil(n / cols) || 1;
          const cellW = 100 / cols;
          const cellH = 100 / rows;
          const padPct = 6;
          return sections.map((s, idx) => {
            const row = Math.floor(idx / cols);
            const col = idx % cols;
            const x0 = col * cellW + padPct / 2;
            const y0 = row * cellH + padPct / 2;
            const x1 = (col + 1) * cellW - padPct / 2;
            const y1 = (row + 1) * cellH - padPct / 2;
            return {
              ...s,
              polygon: [
                { x: x0, y: y0 },
                { x: x1, y: y0 },
                { x: x1, y: y1 },
                { x: x0, y: y1 },
              ],
            };
          });
        })();

    const planeFillRgba = [
      'rgba(59,130,246,0.18)',
      'rgba(34,197,94,0.18)',
      'rgba(168,85,247,0.18)',
      'rgba(249,115,22,0.18)',
      'rgba(239,68,68,0.18)',
      'rgba(6,182,212,0.18)',
      'rgba(236,72,153,0.18)',
    ];
    const planeStroke = '#4b5563';

    // Draw roof planes
    sectionsWithPoly.forEach((section, idx) => {
      if (!section.polygon || section.polygon.length < 3) return;
      ctx.beginPath();
      let cx = 0;
      let cy = 0;
      section.polygon.forEach((p, i) => {
        const x = toX(p.x);
        const y = toY(p.y);
        cx += x;
        cy += y;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.fillStyle = planeFillRgba[idx % planeFillRgba.length];
      ctx.strokeStyle = planeStroke;
      ctx.lineWidth = Math.max(1.5, canvasWidth / 700);
      ctx.fill();
      ctx.stroke();

      // Label with area or name in the centroid-ish
      const n = section.polygon.length;
      cx /= n;
      cy /= n;
      const label =
        (typeof section.area === 'number' && Number.isFinite(section.area)
          ? Math.round(section.area).toString()
          : section.name || String.fromCharCode(65 + (idx % 26)));
      ctx.fillStyle = '#111827';
      ctx.font = `${Math.max(11, canvasWidth / 40)}px Helvetica, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, cx, cy);
    });

    // Draw structural lines by type for color-coding
    if (structuralLinesData) {
      const hipLines = structuralLinesData.hipLines ?? [];
      const ridgeLines = structuralLinesData.ridgeLines ?? [];
      const valleyLines = structuralLinesData.valleyLines ?? [];
      const eaveLines = structuralLinesData.eaveLines ?? [];
      const rakeLines = structuralLinesData.rakeLines ?? [];

      const drawLines = (lines: StructuralLineSegment[] | undefined, color: string) => {
        if (!lines) return;
        ctx.strokeStyle = color;
        ctx.lineWidth = Math.max(1, canvasWidth / 800);
        ctx.lineCap = 'round';
        lines.forEach((seg) => {
          const x1 = toX(seg.x1);
          const y1 = toY(seg.y1);
          const x2 = toX(seg.x2);
          const y2 = toY(seg.y2);
          if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) return;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
        });
      };

      drawLines(ridgeLines, '#3b82f6');   // blue
      drawLines(hipLines, '#f97316');     // orange
      drawLines(valleyLines, '#ef4444');  // red
      drawLines(eaveLines, '#22c55e');    // green
      drawLines(rakeLines, '#a855f7');    // purple
    }

    try {
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  };

  /** Draw primary image + polygons + structural lines to canvas; return JPEG data URL for PDF */
  const drawMarkedRoofDiagram = (
    primaryImageUrl: string,
    sections: RoofSection[],
    structuralLinesData: StructuralLines,
    canvasWidth: number,
    canvasHeight: number
  ): Promise<string | null> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
        const toX = (x: number) => (Number(x) / 100) * canvasWidth;
        const toY = (y: number) => (Number(y) / 100) * canvasHeight;
        const planeFillRgba = ['rgba(59,130,246,0.35)', 'rgba(34,197,94,0.35)', 'rgba(168,85,247,0.35)', 'rgba(249,115,22,0.35)', 'rgba(239,68,68,0.35)', 'rgba(6,182,212,0.35)', 'rgba(236,72,153,0.35)'];
        sections.forEach((section, idx) => {
          if (!section.polygon || section.polygon.length < 3) return;
          ctx.beginPath();
          section.polygon.forEach((p, i) => {
            const x = toX(p.x);
            const y = toY(p.y);
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          });
          ctx.closePath();
          ctx.fillStyle = planeFillRgba[idx % planeFillRgba.length];
          ctx.fill();
        });
        const hipLines = structuralLinesData.hipLines ?? [];
        const ridgeLines = structuralLinesData.ridgeLines ?? [];
        const valleyLines = structuralLinesData.valleyLines ?? [];
        const eaveLines = structuralLinesData.eaveLines ?? [];
        const rakeLines = structuralLinesData.rakeLines ?? [];
        const allLines = [...hipLines, ...ridgeLines, ...valleyLines, ...eaveLines, ...rakeLines];
        ctx.strokeStyle = '#ea580c';
        ctx.lineWidth = Math.max(1, canvasWidth / 700);
        ctx.lineCap = 'round';
        if (allLines.length > 0) {
          allLines.forEach((seg) => {
            const x1 = toX(seg.x1);
            const y1 = toY(seg.y1);
            const x2 = toX(seg.x2);
            const y2 = toY(seg.y2);
            if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) return;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
          });
        } else {
          sections.forEach((section) => {
            if (!section.polygon || section.polygon.length < 3) return;
            ctx.beginPath();
            section.polygon.forEach((p, i) => {
              const x = toX(p.x);
              const y = toY(p.y);
              if (i === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            });
            ctx.closePath();
            ctx.strokeStyle = '#ea580c';
            ctx.stroke();
          });
        }
        try {
          resolve(canvas.toDataURL('image/jpeg', 0.92));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = primaryImageUrl;
    });
  };

  const generatePDFReport = async (clientName?: string) => {
    if (!analysis) {
      toast({
        title: "No analysis available",
        description: "Please generate a roof report first",
        variant: "destructive"
      });
      return;
    }

    try {
      const safeNumber = (value: unknown, fallback = 0): number => {
        const n = typeof value === 'number' ? value : Number(value);
        return Number.isFinite(n) ? n : fallback;
      };

      const safeToLocale = (value: unknown, options?: Intl.NumberFormatOptions) => {
        return safeNumber(value, 0).toLocaleString(undefined, options);
      };

      // Resolve company name from current user or localStorage fallback
      let companyName =
        (typeof (user as any)?.companyName === 'string' && (user as any).companyName.trim()) ||
        (typeof (user as any)?.company_name === 'string' && (user as any).company_name.trim()) ||
        (typeof (user as any)?.contractor?.companyName === 'string' && (user as any).contractor.companyName.trim()) ||
        (typeof (user as any)?.contractor?.company_name === 'string' && (user as any).contractor.company_name.trim()) ||
        '';

      if (!companyName && typeof window !== 'undefined') {
        try {
          const raw = window.localStorage.getItem('user');
          if (raw) {
            const parsed = JSON.parse(raw);
            companyName =
              (typeof parsed.companyName === 'string' && parsed.companyName.trim()) ||
              (typeof parsed.company_name === 'string' && parsed.company_name.trim()) ||
              (typeof parsed.contractor?.companyName === 'string' && parsed.contractor.companyName.trim()) ||
              (typeof parsed.contractor?.company_name === 'string' && parsed.contractor.company_name.trim()) ||
              '';
          }
        } catch {
          // ignore localStorage errors
        }
      }

      // Fresh DB value (localStorage is often stale on company name)
      try {
        const res = await apiRequest('GET', '/api/user');
        const data = await res.json();
        const u = data?.user ?? data;
        const fromApi =
          (typeof u?.companyName === 'string' && u.companyName.trim()) ||
          (typeof u?.company_name === 'string' && u.company_name.trim()) ||
          '';
        if (fromApi) companyName = fromApi;
      } catch {
        /* keep cached */
      }

      console.log('[Roof PDF] Resolved company name:', {
        fromUser: (user as any)?.companyName,
        fromUserSnake: (user as any)?.company_name,
        final: companyName
      });

      // Dynamically import jsPDF — force mm + A4 so header/footer coords match all environments
      const jsPDF = (await import('jspdf')).default;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 24;
      const topMargin = 28;
      const bottomMargin = 22;
      const footerH = 12;
      const contentBottom = pageHeight - bottomMargin - footerH;
      let yPos = margin;

      const valueCol = 52;

      const addHeader = (d: any, pageNum: number, totalPages: number) => {
        d.setFontSize(8);
        d.setFont('helvetica', 'normal');
        d.setTextColor(100, 100, 100);
        d.text('Remodra Roof Report', margin, topMargin - 10);
        d.text(`Page ${pageNum} of ${totalPages}  ·  ${new Date().toLocaleDateString()}`, pageWidth - margin - 55, topMargin - 10);
        d.setTextColor(15, 23, 42);
      };

      const addFooter = (d: any, pageNum: number, totalPages: number) => {
        const ph =
          typeof d.internal.pageSize.getHeight === 'function'
            ? d.internal.pageSize.getHeight()
            : (d.internal.pageSize as { height: number }).height;
        const yPrimary = ph - 7;
        d.setFontSize(7.5);
        d.setFont('helvetica', 'normal');
        d.setTextColor(100, 100, 100);
        const company = companyName || 'Your Company';
        d.text(company, margin, yPrimary);
        const domain = 'getremodra.com';
        const domainW = d.getTextWidth(domain);
        d.text(domain, (pageWidth - domainW) / 2, yPrimary);
        d.text(`Page ${pageNum} of ${totalPages}  ·  ${new Date().toLocaleDateString()}`, pageWidth - margin - 55, yPrimary);
        d.setTextColor(15, 23, 42);
      };

      const checkPage = (d: any, yPos: number, minSpace = 50) => {
        if (yPos > contentBottom - minSpace) {
          d.addPage();
          return topMargin;
        }
        return yPos;
      };

      const contentW = pageWidth - 2 * margin;
      /** Pro report: left / mid (right-aligned) / right (right-aligned) */
      const txL = margin + 4;
      const txM = margin + contentW * 0.58;
      const txR = margin + contentW - 4;

      /** Section heading + hairline — no filled bars or grids */
      const pdfProH2 = (d: any, title: string, yTop: number) => {
        d.setFontSize(12);
        d.setFont('helvetica', 'bold');
        d.setTextColor(30, 41, 59);
        d.text(title, margin, yTop + 5);
        d.setDrawColor(203, 213, 225);
        d.setLineWidth(0.35);
        d.line(margin, yTop + 8.5, margin + contentW, yTop + 8.5);
        d.setFont('helvetica', 'normal');
        d.setTextColor(15, 23, 42);
        return yTop + 14;
      };

      /** Three columns: headers + rule + body rows (typographic, no boxes/zebra) */
      const pdfProTable3 = (
        d: any,
        y0: number,
        headers: [string, string, string],
        rows: [string, string, string][]
      ) => {
        let y = y0;
        d.setFontSize(8);
        d.setFont('helvetica', 'bold');
        d.setTextColor(100, 116, 139);
        d.text(headers[0], txL, y + 4);
        d.text(headers[1], txM, y + 4, { align: 'right' });
        d.text(headers[2], txR, y + 4, { align: 'right' });
        y += 7;
        d.setDrawColor(203, 213, 225);
        d.setLineWidth(0.45);
        d.line(margin, y, margin + contentW, y);
        y += 4;
        d.setFontSize(10);
        d.setFont('helvetica', 'normal');
        d.setTextColor(15, 23, 42);
        rows.forEach((row) => {
          d.text(row[0], txL, y + 5);
          d.text(row[1], txM, y + 5, { align: 'right' });
          d.text(row[2], txR, y + 5, { align: 'right' });
          y += 7.2;
        });
        return y + 4;
      };

      /** Materials line items: name | qty+unit | $/unit | line total */
      const pdfMaterialsList = (d: any, y0: number, mats: MaterialBreakdown[]) => {
        if (!mats.length) return y0;
        const c2 = margin + contentW * 0.42;
        const c3 = margin + contentW * 0.64;
        const c4 = margin + contentW - 4;
        let y = y0;
        d.setFontSize(8);
        d.setFont('helvetica', 'bold');
        d.setTextColor(100, 116, 139);
        d.text('Material', txL, y + 4);
        d.text('Qty', c2, y + 4, { align: 'right' });
        d.text('$/unit', c3, y + 4, { align: 'right' });
        d.text('Total', c4, y + 4, { align: 'right' });
        y += 7;
        d.setDrawColor(203, 213, 225);
        d.setLineWidth(0.45);
        d.line(margin, y, margin + contentW, y);
        y += 4;
        d.setFontSize(9);
        d.setFont('helvetica', 'normal');
        d.setTextColor(15, 23, 42);
        mats.forEach((m) => {
          const raw = (m.name || '').trim() || '—';
          const shortName = raw.length > 46 ? `${raw.slice(0, 44)}…` : raw;
          d.text(shortName, txL, y + 5);
          d.text(`${safeNumber(m.quantity, 0)} ${m.unit}`.trim(), c2, y + 5, { align: 'right' });
          d.text(`$${safeNumber(m.unitPrice, 0).toFixed(2)}`, c3, y + 5, { align: 'right' });
          d.text(
            `$${safeToLocale(m.total, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            c4,
            y + 5,
            { align: 'right' }
          );
          y += 7.2;
        });
        return y + 4;
      };

      const sectionTitle = (d: any, title: string, yRef: { current: number }) => {
        yRef.current = checkPage(d, yRef.current, 30);
        d.setFillColor(248, 250, 252);
        d.rect(0, yRef.current - 5, pageWidth, 10, 'F');
        d.setFontSize(12);
        d.setFont('helvetica', 'bold');
        d.setTextColor(15, 23, 42);
        d.text(title, margin, yRef.current + 3);
        yRef.current += 14;
      };

      const facetCount = (analysis.roofSections?.length ?? 0);
      const predominantPitch = analysis.pitchRatio || '—';
      const totalSqft = safeNumber(analysis.squareFootage, 0);
      const totalSquares = safeNumber(analysis.squares, totalSqft / 100);
      const reportDate = new Date().toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
      const isSolar = analysis.source === 'google_solar';
      const prov = analysis.measurementProvenance;
      const solarSum = analysis.solarPotentialSummary;
      const orderDisclaimer = analysis.orderingGradeDisclaimer;

      const formatSunQ = (q: number[] | undefined | null): string => {
        if (!q || q.length < 3) return '—';
        const lo = q[0];
        const mid = q[Math.floor(q.length / 2)];
        const hi = q[q.length - 1];
        return `${lo.toFixed(2)} / ${mid.toFixed(2)} / ${hi.toFixed(2)}`;
      };

      // ----- COVER PAGE -----
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pageWidth, 88, 'F');
      doc.setDrawColor(148, 163, 184);
      doc.setLineWidth(1.2);
      doc.line(0, 88, pageWidth, 88);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('PROFESSIONAL ROOF MEASUREMENT', margin, 18);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('ROOF MEASUREMENT REPORT', margin, 34);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(200, 210, 230);
      doc.text(isSolar ? 'Aerial-derived roof model' : 'Image-based analysis', margin, 44);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.text(reportDate, margin, 54);
      doc.setFontSize(10);
      doc.setTextColor(220, 220, 220);
      const preparedBy = companyName || 'Remodra';
      doc.text(`Prepared by ${preparedBy}`, margin, 64);
      doc.setTextColor(255, 255, 255);
      // Stats on the right — below the title row so they never overlap "REPORT"
      doc.setFont('helvetica', 'bold');
      doc.text(`${safeToLocale(totalSqft)} sq ft`, pageWidth - margin, 52, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`${facetCount} facets · ${predominantPitch}`, pageWidth - margin, 60, { align: 'right' });
      yPos = 92;

      // Client / Company — labels aligned; values share one column (valueCol)
      const clientDisplay = String(clientName ?? '').trim() || '—';
      const companyText = companyName.trim() || '—';
      doc.setFontSize(10);
      const companyLines = doc.splitTextToSize(companyText, pageWidth - margin - valueCol - 4);
      const clientRowY = yPos + 7;
      const companyStartY = yPos + 16;
      let cyAfterCompany = companyStartY + Math.max(1, companyLines.length) * 4.5;
      const blockH = Math.max(cyAfterCompany, clientRowY + 5) - yPos + 8;
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.rect(margin, yPos - 2, pageWidth - 2 * margin, blockH, 'S');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Client:', margin + 4, clientRowY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(51, 65, 85);
      doc.text(clientDisplay, valueCol, clientRowY);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Company:', margin + 4, companyStartY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(51, 65, 85);
      let cy = companyStartY;
      companyLines.forEach((line) => {
        doc.text(line, valueCol, cy);
        cy += 4.5;
      });
      doc.setTextColor(15, 23, 42);
      yPos += blockH + 6;

      // Property address — value on same line as label (aligned with Client/Company)
      const addressStr = String(reportAddress ?? '').trim();
      if (addressStr) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Property:', margin + 4, yPos + 5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(51, 65, 85);
        const addrLines = doc.splitTextToSize(addressStr, pageWidth - margin - valueCol - 4);
        let ay = yPos + 5;
        addrLines.forEach((line) => {
          doc.text(line, valueCol, ay);
          ay += 4.5;
        });
        yPos = ay + 4;
      } else {
        yPos += 6;
      }

      // Aerial / roof image directly under property (minimal caption)
      const primaryImageForPdf = uploadedImages[analysis.primaryImageIndex ?? 0] ?? uploadedImages[0];
      const satelliteForPdf = (analysis as RoofAnalysis & { satelliteImageDataUrl?: string | null }).satelliteImageDataUrl;
      const roofPhotoDataUrl = primaryImageForPdf || (analysis.source === 'google_solar' ? satelliteForPdf : null) || null;
      if (roofPhotoDataUrl) {
        const reserveDataSource = isSolar && prov ? 38 : 10;
        // Keep the image on page 1 when possible. Only move to the next page if
        // there's truly not enough vertical room to render it at a usable size.
        const minUsableImageH = 72;
        const minImageBlock = minUsableImageH + reserveDataSource + 12; // caption + padding + data source block
        if (yPos > contentBottom - minImageBlock) {
          doc.addPage();
          yPos = topMargin;
        }
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(
          analysis.source === 'google_solar'
            ? 'Aerial view'
            : 'Roof image from analysis',
          margin + 4,
          yPos + 3
        );
        doc.setTextColor(15, 23, 42);
        yPos += 5;
        try {
          const img = new Image();
          img.src = roofPhotoDataUrl;
          await new Promise<void>((resolve) => {
            img.onload = () => {
              try {
                const boxW = pageWidth - 2 * margin;
                const availableH = contentBottom - yPos - reserveDataSource;
                const maxH = Math.max(minUsableImageH, availableH);
                // Scale uniformly to use the full width OR full height of the slot (whichever binds first).
                const scale = Math.min(boxW / img.width, maxH / img.height);
                const drawW = img.width * scale;
                const drawH = img.height * scale;
                const xImg = margin + (boxW - drawW) / 2;
                const format = roofPhotoDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
                doc.addImage(roofPhotoDataUrl, format, xImg, yPos, drawW, drawH);
                yPos += drawH + 6;
              } catch (e) {
                console.warn('PDF addImage roof photo failed:', e);
              }
              resolve();
            };
            img.onerror = () => {
              console.warn('PDF roof image failed to load in browser');
              resolve();
            };
          });
        } catch (e) {
          console.warn('PDF roof photo failed:', e);
        }
      } else if (analysis.source === 'google_solar' && !roofPhotoDataUrl) {
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('Aerial preview unavailable (configure Maps Static API on the server key).', margin + 4, yPos + 4);
        doc.setTextColor(15, 23, 42);
        yPos += 10;
      }

      // Data source (compact) — after image so page 1 flows: client → property → photo → source
      if (isSolar && prov) {
        if (yPos > contentBottom - 36) {
          doc.addPage();
          yPos = topMargin;
        }
        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(226, 232, 240);
        doc.rect(margin, yPos, pageWidth - 2 * margin, 28, 'FD');
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 65, 85);
        doc.text('Data source', margin + 4, yPos + 6);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);
        doc.text('Address-based aerial roof model', margin + 4, yPos + 12);
        const cc =
          prov.centerLat != null && prov.centerLng != null
            ? `${prov.centerLat.toFixed(5)}, ${prov.centerLng.toFixed(5)}`
            : '—';
        doc.text(`Center: ${cc}`, margin + 4, yPos + 18);
        doc.setTextColor(15, 23, 42);
        yPos += 32;
      }

      // Start SUMMARY and the rest of the report on a NEW PAGE (page 2+)
      doc.addPage();
      yPos = topMargin;

      if (isSolar && orderDisclaimer) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 41, 59);
        doc.text('Methodology', margin, yPos);
        yPos += 5;
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);
        const discLines = doc.splitTextToSize(orderDisclaimer, pageWidth - 2 * margin);
        const maxMeth = 5;
        discLines.slice(0, maxMeth).forEach((line: string) => {
          doc.text(line, margin, yPos);
          yPos += 3.8;
        });
        if (discLines.length > maxMeth) {
          doc.text('…', margin, yPos);
          yPos += 3.8;
        }
        yPos += 4;
        doc.setFontSize(7);
        doc.setTextColor(100, 116, 139);
        const iq = analysis.imageryQuality || '—';
        const ip = prov?.imageryProcessedDate || '—';
        doc.text(`Imagery: ${iq} · Processed: ${ip}`, margin, yPos);
        yPos += 8;
        doc.setTextColor(15, 23, 42);
      } else if (isSolar && !orderDisclaimer) {
        doc.setFontSize(7.5);
        doc.setTextColor(100, 116, 139);
        doc.text(
          'Aerial data may be incomplete — request a new roof report after your server is updated.',
          margin,
          yPos
        );
        yPos += 10;
        doc.setTextColor(15, 23, 42);
      }

      // ----- SUMMARY (page 2) -----
      // Use a slightly stronger background so the box is clearly visible.
      doc.setFillColor(230, 242, 255);
      doc.rect(margin, yPos - 2, pageWidth - 2 * margin, 38, 'F');
      doc.setDrawColor(148, 163, 184);
      doc.rect(margin, yPos - 2, pageWidth - 2 * margin, 38, 'S');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Summary', margin + 4, yPos + 8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total roof area: ${safeToLocale(totalSqft)} sq ft (${totalSquares.toFixed(1)} squares)`, margin + 4, yPos + 18);
      doc.text(
        `Roof type: ${analysis.roofType}  ·  Complexity: ${analysis.complexity}  ·  ${
          isSolar ? `Imagery: ${imageryLabel(analysis.imageryQuality)}` : `Confidence: ${confidenceLabel(analysis.confidence)}`
        }`,
        margin + 4,
        yPos + 26
      );
      doc.text(`Existing layers: ${safeNumber(analysis.existingLayers, 1)}`, margin + 4, yPos + 34);
      yPos += 48;

      // Facet schedule (Google Solar — modelled facets with pitch & azimuth from API)
      if (isSolar && (analysis.roofSections?.length ?? 0) > 0) {
        yPos = checkPage(doc, yPos, 100);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);
        doc.text('Facet measurement schedule', margin, yPos);
        yPos += 10;
        doc.setFontSize(7.5);
        doc.setTextColor(100, 116, 139);
        doc.text('Segment-based roof areas and orientation. Sun columns: min / median / max where available.', margin, yPos);
        yPos += 6;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(30, 41, 59);
        const colFacet = margin;
        const colArea = margin + 28;
        const colSq = margin + 58;
        const colPitch = margin + 78;
        const colAz = margin + 102;
        const colDir = margin + 122;
        const colSun = margin + 138;
        doc.text('Facet', colFacet, yPos);
        doc.text('Area', colArea, yPos);
        doc.text('Sq', colSq, yPos);
        doc.text('Pitch', colPitch, yPos);
        doc.text('Az°', colAz, yPos);
        doc.text('Dir', colDir, yPos);
        doc.text('Sun min/med/max', colSun, yPos);
        yPos += 5;
        doc.setDrawColor(203, 213, 225);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 4;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(15, 23, 42);
        (analysis.roofSections ?? []).forEach((s: RoofSection) => {
          if (yPos > contentBottom - 20) {
            doc.addPage();
            yPos = topMargin;
          }
          const sq = (safeNumber(s.area, 0) / 100).toFixed(2);
          doc.text(s.name.replace('Facet ', 'F'), colFacet, yPos);
          doc.text(safeToLocale(s.area), colArea, yPos);
          doc.text(sq, colSq, yPos);
          doc.text(`${s.pitchRatio} (${safeNumber(s.pitch, 0)}°)`, colPitch, yPos);
          doc.text(s.azimuthDegrees != null ? String(Math.round(s.azimuthDegrees * 10) / 10) : '—', colAz, yPos);
          doc.text(s.azimuthLabel || '—', colDir, yPos);
          doc.text(formatSunQ(s.sunshineQuantiles ?? null), colSun, yPos);
          yPos += 6;
        });
        yPos += 10;
      }

      // Length measurement report — when we have area, always show Ridge, Eave, Valley (use estimates when not measured)
      const totalSqftForLinear = safeNumber(analysis.squareFootage, 0);
      let ridge = safeNumber(analysis.ridgeLength);
      let eave = safeNumber(analysis.eaveLength);
      const rake = safeNumber(analysis.rakeLength);
      const hip = safeNumber(analysis.hipLength);
      let valley = safeNumber(analysis.valleyLength);
      const stepFlash = safeNumber(analysis.stepFlashing);
      const apronFlash = safeNumber(analysis.apronFlashing);
      if (totalSqftForLinear > 0) {
        if (ridge === 0) ridge = Math.round(Math.sqrt(totalSqftForLinear));
        if (eave === 0) eave = Math.round(4 * Math.sqrt(totalSqftForLinear));
        if (valley === 0 && (analysis.roofSections?.length ?? 0) > 2) valley = Math.round(Math.sqrt(totalSqftForLinear) * 0.5);
      }
      const hasLinear = ridge > 0 || eave > 0 || rake > 0 || hip > 0 || valley > 0 || stepFlash > 0 || apronFlash > 0;

      if (hasLinear) {
        yPos = checkPage(doc, yPos, 80);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(isSolar ? 'Estimated linear quantities' : 'Length Measurement Report', margin, yPos);
        yPos += 8;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);
        if (isSolar) {
          doc.text(
            'Ridge, valley, and hip line totals below are Remodra estimates (not traced edge lengths from the API). Use facet areas above for primary ordering.',
            margin,
            yPos
          );
          yPos += 6;
        }
        doc.setTextColor(15, 23, 42);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Ridges: ${safeToLocale(ridge)} ft${!analysis.ridgeLength ? ' (est. from area)' : ''}`, margin, yPos);
        yPos += 6;
        doc.text(`Eaves: ${safeToLocale(eave)} ft${!analysis.eaveLength ? ' (est. from area)' : ''}`, margin, yPos);
        yPos += 6;
        doc.text(`Valleys: ${safeToLocale(valley)} ft`, margin, yPos);
        yPos += 6;
        const linearItems: [string, number][] = [
          ['Hips:', hip],
          ['Rakes:', rake],
          ['Step flashing:', stepFlash],
          ['Wall flashing:', apronFlash]
        ];
        linearItems.forEach(([label, val]) => {
          if (val > 0) {
            doc.text(`${label} ${safeToLocale(val)} ft`, margin, yPos);
            yPos += 6;
          }
        });
        doc.setFont('helvetica', 'bold');
        doc.text(`Hips + Ridges: ${safeToLocale(hip + ridge)} ft`, margin, yPos);
        yPos += 6;
        doc.text(`Eaves + Rakes: ${safeToLocale(eave + rake)} ft`, margin, yPos);
        yPos += 10;
      }

      // Area by pitch (from roofSections)
      const sections = analysis.roofSections ?? [];
      const pitchMap: Record<string, number> = {};
      sections.forEach((s: RoofSection) => {
        const ratio = s.pitchRatio || '—';
        pitchMap[ratio] = (pitchMap[ratio] || 0) + safeNumber(s.area, 0);
      });
      const pitchEntries = Object.entries(pitchMap).sort((a, b) => {
        const parse = (r: string) => { const n = r.split('/'); return n.length === 2 ? Number(n[0]) / Number(n[1]) : 0; };
        return parse(a[0]) - parse(b[0]);
      });

      if (pitchEntries.length > 0) {
        yPos = checkPage(doc, yPos, 70);
        yPos = pdfProH2(doc, 'Area by Pitch', yPos);
        const pitchRows: [string, string, string][] = pitchEntries.map(([ratio, area]) => [
          ratio,
          safeToLocale(area),
          (area / 100).toFixed(1),
        ]);
        yPos = pdfProTable3(doc, yPos, ['Pitch', 'Area (sq ft)', 'Squares'], pitchRows);
      }

      // Waste % table (totals already in Summary above)
      yPos = checkPage(doc, yPos, 88);
      yPos = pdfProH2(doc, 'Waste factors (asphalt)', yPos);
      const wastePcts = [0, 10, 12, 15, 17, 20, 22];
      const wasteRows: [string, string, string][] = wastePcts.map((pct) => {
        const withWaste = totalSqft * (1 + pct / 100);
        return [`${pct}%`, safeToLocale(Math.round(withWaste)), (withWaste / 100).toFixed(1)];
      });
      yPos = pdfProTable3(doc, yPos, ['Waste %', 'Area (sq ft)', 'Squares'], wasteRows);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 116, 139);
      const wasteNote = doc.splitTextToSize(
        'Recommended waste is based on asphalt shingle roof. Adjust by complexity and valley system.',
        contentW - 8
      );
      wasteNote.forEach((line: string) => {
        doc.text(line, margin + 4, yPos + 3);
        yPos += 3.8;
      });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      yPos += 6;

      // Material estimates — same pro typography, two-column facts (no boxes)
      yPos = checkPage(doc, yPos, 85);
      yPos = pdfProH2(doc, 'Material estimates', yPos);
      const waste10 = Math.round(totalSqft * 1.1);
      const waste15 = Math.round(totalSqft * 1.15);
      const waste17 = Math.round(totalSqft * 1.17);
      const waste20 = Math.round(totalSqft * 1.2);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text('Shingle coverage (total sq ft by waste %)', margin, yPos + 4);
      yPos += 8;
      const shingleMatRows: [string, string][] = [
        ['10%', `${safeToLocale(waste10)} sq ft`],
        ['15%', `${safeToLocale(waste15)} sq ft`],
        ['17%', `${safeToLocale(waste17)} sq ft`],
        ['20%', `${safeToLocale(waste20)} sq ft`],
      ];
      shingleMatRows.forEach(([pct, val]) => {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(15, 23, 42);
        doc.text(pct, txL, yPos + 5);
        doc.text(val, txR, yPos + 5, { align: 'right' });
        yPos += 7;
      });
      yPos += 6;
      const matLineItems: [string, number][] = [];
      const starterFt = eave + rake;
      if (starterFt > 0) matLineItems.push(['Starter (eaves + rakes)', starterFt]);
      const iceWaterFt = eave + valley + stepFlash + apronFlash;
      if (iceWaterFt > 0) matLineItems.push(['Ice & water (eaves, valleys, flashings)', iceWaterFt]);
      const cappingFt = hip + ridge;
      if (cappingFt > 0) matLineItems.push(['Capping (hips + ridges)', cappingFt]);
      matLineItems.forEach(([label, ft]) => {
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);
        doc.text(label, txL, yPos + 5);
        doc.setFontSize(10);
        doc.setTextColor(30, 41, 59);
        doc.text(`${safeToLocale(ft)} ft`, txR, yPos + 5, { align: 'right' });
        yPos += 7;
      });
      yPos += 4;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 116, 139);
      doc.text('These calculations are estimates. Verify before ordering.', margin + 4, yPos + 3);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(15, 23, 42);
      yPos += 10;

      // Features Section
      yPos = checkPage(doc, yPos, 60);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Detected Features', margin, yPos);
      yPos += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const feat = analysis.features ?? defaultFeatures;
      const features = [];
      if ((feat.chimneys ?? 0) > 0) features.push(`Chimneys: ${feat.chimneys}`);
      if ((feat.skylights ?? 0) > 0) features.push(`Skylights: ${feat.skylights}`);
      if ((feat.vents ?? 0) > 0) features.push(`Vents: ${feat.vents}`);
      if ((feat.dormers ?? 0) > 0) features.push(`Dormers: ${feat.dormers}`);
      if ((feat.valleys ?? 0) > 0) features.push(`Valleys: ${feat.valleys}`);
      if ((feat.hips ?? 0) > 0) features.push(`Hips: ${feat.hips}`);
      if ((feat.ridges ?? 0) > 0) features.push(`Ridges: ${feat.ridges}`);

      if (features.length > 0) {
        features.forEach(feature => {
          doc.text(`• ${feature}`, margin, yPos);
          yPos += 7;
        });
      } else {
        doc.text('No features detected', margin, yPos);
        yPos += 7;
      }

      yPos += 5;

      // Condition Assessment
      if (analysis.conditionDetails) {
        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = topMargin;
        }

        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Condition Assessment', margin, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setFont('helvetica', 'bold');
        doc.text(`Overall Status: ${analysis.conditionDetails.overallStatus.charAt(0).toUpperCase() + analysis.conditionDetails.overallStatus.slice(1)}`, margin, yPos);
        yPos += 7;
        doc.setFont('helvetica', 'normal');
        doc.text(`Urgency: ${analysis.conditionDetails.urgency.charAt(0).toUpperCase() + analysis.conditionDetails.urgency.slice(1)}`, margin, yPos);
        yPos += 10;

        if (analysis.conditionDetails.detectedIssues && analysis.conditionDetails.detectedIssues.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.text('Detected Issues:', margin, yPos);
          yPos += 7;
          doc.setFont('helvetica', 'normal');
          analysis.conditionDetails.detectedIssues.forEach(issue => {
            const lines = doc.splitTextToSize(`• ${issue}`, pageWidth - 2 * margin);
            lines.forEach((line: string) => {
              if (yPos > pageHeight - 30) {
                doc.addPage();
                yPos = topMargin;
              }
              doc.text(line, margin, yPos);
              yPos += 7;
            });
          });
          yPos += 5;
        }

        if (analysis.conditionDetails.recommendations && analysis.conditionDetails.recommendations.length > 0) {
          if (yPos > pageHeight - 30) {
            doc.addPage();
            yPos = topMargin;
          }
          doc.setFont('helvetica', 'bold');
          doc.text('Recommendations:', margin, yPos);
          yPos += 7;
          doc.setFont('helvetica', 'normal');
          analysis.conditionDetails.recommendations.forEach(rec => {
            const lines = doc.splitTextToSize(`• ${rec}`, pageWidth - 2 * margin);
            lines.forEach((line: string) => {
              if (yPos > pageHeight - 30) {
                doc.addPage();
                yPos = topMargin;
              }
              doc.text(line, margin, yPos);
              yPos += 7;
            });
          });
        }
      }

      // Roof Diagram (Marked) — primary image with polygons + structural lines.
      // For google_solar reports we skip this entirely (we only have stats, not true geometry).
      if (analysis.source !== 'google_solar') {
        const primaryImageIndex = analysis.primaryImageIndex ?? 0;
        const primaryImageUrl = uploadedImages[primaryImageIndex];
        const roofSections = analysis.roofSections ?? [];
        const manualSectionsForPdf: RoofSection[] = manualPolygons.length > 0
          ? manualPolygons.map((p) => ({
              name: p.name,
              type: 'manual',
              area: 0,
              pitch: 0,
              pitchRatio: '',
              polygon: p.points,
              bounds: undefined,
              visibleInImages: [primaryImageIndex]
            }))
          : [];
        const structuralLinesData: StructuralLines = (() => {
          const base = analysis.structuralLines ?? {};
          if (manualLines.length === 0) return base;
          const segs = manualLines.map((l) => ({ x1: l.p1.x, y1: l.p1.y, x2: l.p2.x, y2: l.p2.y, label: l.type }));
          return {
            ...base,
            hipLines: [...(base.hipLines ?? []), ...segs.filter(s => s.label === 'hip')],
            ridgeLines: [...(base.ridgeLines ?? []), ...segs.filter(s => s.label === 'ridge')],
            valleyLines: [...(base.valleyLines ?? []), ...segs.filter(s => s.label === 'valley')],
            eaveLines: [...(base.eaveLines ?? []), ...segs.filter(s => s.label === 'eave')],
            rakeLines: [...(base.rakeLines ?? []), ...segs.filter(s => s.label === 'rake')],
          };
        })();

        // Roof diagram image: prefer simplified plan diagram (no photo),
        // fall back to photo-based marked diagram if needed.
        if (yPos > pageHeight - 120) {
          doc.addPage();
          yPos = topMargin;
        }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Roof Diagram', margin, yPos);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        doc.text('Simplified roof plan diagram from analysis (sections + hips/ridges/valleys).', margin, yPos + 6);
        doc.setTextColor(15, 23, 42);
        yPos += 14;

        let diagramDataUrl: string | null = null;
        const sectionsForDiagram = manualSectionsForPdf.length > 0 ? manualSectionsForPdf : roofSections;

        if (sectionsForDiagram.length > 0) {
          diagramDataUrl = drawRoofPlanDiagram(sectionsForDiagram, structuralLinesData, 800, 500);
        }

        if (!diagramDataUrl && primaryImageUrl) {
          try {
            diagramDataUrl = await drawMarkedRoofDiagram(
              primaryImageUrl,
              sectionsForDiagram,
              structuralLinesData,
              800,
              500
            );
          } catch (e) {
            console.error('Marked diagram image failed:', e);
          }
        }

        if (diagramDataUrl) {
          const imgW = pageWidth - 2 * margin;
          const imgH = Math.min(120, (500 / 800) * imgW);
          const format = diagramDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
          doc.addImage(diagramDataUrl, format, margin, yPos, imgW, imgH);
          yPos += imgH + 12;
        }
      }

      // Add images
      if (uploadedImages.length > 0) {
        for (let i = 0; i < uploadedImages.length; i++) {
          if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = topMargin;
          }

          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(`Roof Image ${i + 1}`, margin, yPos);
          yPos += 10;

          try {
            const img = new Image();
            img.src = uploadedImages[i];
            
            await new Promise((resolve) => {
              img.onload = () => {
                const imgWidth = pageWidth - 2 * margin;
                const imgHeight = (img.height * imgWidth) / img.width;
                
                if (imgHeight > pageHeight - yPos - 20) {
                  // Image too tall, scale it down
                  const scaledHeight = pageHeight - yPos - 20;
                  const scaledWidth = (img.width * scaledHeight) / img.height;
                  doc.addImage(uploadedImages[i], 'JPEG', margin, yPos, scaledWidth, scaledHeight);
                  yPos += scaledHeight + 10;
                } else {
                  doc.addImage(uploadedImages[i], 'JPEG', margin, yPos, imgWidth, imgHeight);
                  yPos += imgHeight + 10;
                }
                resolve(null);
              };
              img.onerror = () => resolve(null); // Skip if image fails to load
            });
          } catch (error) {
            console.error(`Failed to add image ${i + 1}:`, error);
          }
        }
      }

      // Pricing Summary (if available)
      if (Object.keys(pricingTiers).length > 0 && selectedTier) {
        const tierData = pricingTiers[selectedTier];
        if (tierData) {
          const materialsSubtotal = Array.isArray(tierData.materials)
            ? tierData.materials.reduce((sum, m) => sum + safeNumber(m.total, 0), 0)
            : 0;

          if (yPos > pageHeight - 100) {
            doc.addPage();
            yPos = topMargin;
          }

          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text(`${tierData.name} Package Pricing`, margin, yPos);
          yPos += 10;

          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.text(`Total: $${safeToLocale(tierData.total, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPos);
          yPos += 7;
          doc.text(`Materials: $${safeToLocale(materialsSubtotal, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPos);
          yPos += 7;
          doc.text(`Labor: $${safeToLocale(tierData.labor, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, margin, yPos);
          yPos += 10;

          const matList = tierData.materials ?? [];
          if (matList.length > 0) {
            yPos = checkPage(doc, yPos, 32 + matList.length * 8);
            yPos = pdfProH2(doc, 'Materials list', yPos);
            yPos = pdfMaterialsList(doc, yPos, matList);
          }
        }
      }

      // Add header (pages 2+) and footer (all pages) in reserved margin zones
      const totalPages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        if (p > 1) addHeader(doc, p, totalPages);
        addFooter(doc, p, totalPages);
      }

      // Save PDF — unique filename so multiple saves don't get (1), (2) or overwrite
      const datePart = new Date().toISOString().split('T')[0];
      const timePart = new Date().toTimeString().slice(0, 8).replace(/:/g, '-');
      const filename = `Remodra-roof-report-${datePart}-${timePart}.pdf`;
      doc.save(filename);

      toast({
        title: "PDF downloaded",
        description: `Saved as ${filename}`,
      });
    } catch (error: any) {
      console.error('PDF generation error:', error);
      toast({
        title: "Error generating PDF",
        description: error.message || "Failed to generate PDF report",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content dashboard-2025 min-h-screen bg-[var(--dash-bg)]">
          <div className="w-full px-4 sm:px-6 lg:px-8 pt-4 pb-6">
            {/* Header */}
            <div className="mb-6">
              <Link href="/dashboard">
                <Button variant="ghost" size="sm" className="mb-4">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Sparkles className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-[var(--dash-text)]">Roof Measurement</h1>
                  <p className="text-[var(--dash-text-muted)]">Roof measurement reports with automatic measurements, diagrams, and cost estimates from aerial data.</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">Enter a property address below and click &quot;Get roof report&quot; to generate an aerial roof measurement.</p>
                </div>
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-6">
                <TabsTrigger value="analysis" className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Analysis
                </TabsTrigger>
                <TabsTrigger value="diagram" className="flex items-center gap-2" disabled={!analysis}>
                  <Layers className="h-4 w-4" />
                  Diagram
                </TabsTrigger>
                <TabsTrigger value="waste" className="flex items-center gap-2" disabled={!analysis}>
                  <Package className="h-4 w-4" />
                  Waste
                </TabsTrigger>
                <TabsTrigger value="pricing" className="flex items-center gap-2" disabled={!analysis}>
                  <Calculator className="h-4 w-4" />
                  Estimate
                </TabsTrigger>
              </TabsList>

              {/* Analysis Tab */}
              <TabsContent value="analysis" className="space-y-4">
                {/* File input - always in DOM */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="roof-image-upload"
                        multiple
                      />

                {/* Get report by address (Google Solar) - same flow as photo: Analysis → Estimate → Send to estimate */}
                {!isAnalyzing && !isFetchingByAddress && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Home className="h-5 w-5" />
                      Aerial roof report by address
                    </CardTitle>
                    <CardDescription>
                      Request a measurement report from aerial imagery for any property address. Results include total area, pitch, segment breakdown, and integrate with Diagram, pricing, and Send to estimate—no photos required.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2 flex-wrap">
                      <Input
                        placeholder="123 Main St, City, State"
                        value={addressInput}
                        onChange={(e) => setAddressInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchReportByAddress()}
                        className="flex-1 min-w-[200px]"
                      />
                      <Button
                        onClick={fetchReportByAddress}
                        disabled={!addressInput.trim()}
                      >
                        <Ruler className="h-4 w-4 mr-2" />
                        Get roof report
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                )}

                {/* Save report action pinned at bottom */}
                {analysis && !isAnalyzing && !isFetchingByAddress && (
                  <div className="mt-6 flex justify-end">
                    <Button 
                      size="sm"
                      type="button"
                      onClick={openRoofPdfDialog}
                      className="px-4"
                    >
                      <FileDown className="h-4 w-4 mr-2" />
                      Save Report as PDF
                    </Button>
                  </div>
                )}

                {/* Analysis Results */}
                {isFetchingByAddress ? (
                  <Card>
                    <CardContent className="py-12">
                      <div className="text-center space-y-4">
                        <Home className="h-12 w-12 mx-auto text-blue-500 animate-pulse" />
                        <div>
                          <h3 className="text-lg font-semibold mb-2">Fetching roof report by address...</h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            Looking up aerial data for this property
                          </p>
                          <Progress value={60} className="w-full max-w-md mx-auto" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : isAnalyzing ? (
                  <Card>
                    <CardContent className="py-12">
                      <div className="text-center space-y-4">
                        <Sparkles className="h-12 w-12 mx-auto text-blue-500 animate-pulse" />
                        <div>
                          <h3 className="text-lg font-semibold mb-2">Analyzing Roof Image...</h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            AI is extracting measurements, features, and condition
                          </p>
                          <Progress value={75} className="w-full max-w-md mx-auto" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : analysis ? (() => {
                  const effectiveAnalysis = getEffectiveAnalysis() || analysis;
                  const safeNum = (v: any, fallback = 0) =>
                    (typeof v === "number" && Number.isFinite(v)) ? v : (Number.isFinite(Number(v)) ? Number(v) : fallback);
                  const safeLocale = (v: any, options?: Intl.NumberFormatOptions) =>
                    safeNum(v, 0).toLocaleString(undefined, options);
                  const isFromAddress = (analysis as RoofAnalysis & { source?: string }).source === 'google_solar';

                  return (
                  <div className="space-y-6">
                      {/* Report source badge when analysis is based on aerial imagery */}
                    {isFromAddress && (
                      <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/30">
                        <CardContent className="py-3 flex items-center gap-2">
                          <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          <span className="font-medium">Remodra aerial roof report</span>
                          <Badge variant="secondary">Aerial measurements</Badge>
                          {(analysis as RoofAnalysis & { imageryDate?: string }).imageryDate && (
                            <span className="text-sm text-muted-foreground">
                              Imagery: {(analysis as RoofAnalysis & { imageryDate?: string }).imageryDate}
                            </span>
                          )}
                        </CardContent>
                      </Card>
                    )}
                    {/* Analyzed Images with Plane Overlay */}
                    {uploadedImages.length > 0 && (
                      <Card className="relative">
                        <CardHeader>
                          <CardTitle>Analyzed Images with Roof Planes ({uploadedImages.length})</CardTitle>
                          <CardDescription>Roof planes highlighted with color-coded overlays matching the diagram</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {uploadedImages.map((img, idx) => {
                              const primaryImageIndex = analysis.primaryImageIndex ?? 0;
                              const isPrimaryImage = idx === primaryImageIndex;
                              const planeColors = ["blue", "green", "purple", "orange", "red", "cyan", "pink"];
                              
                              // Show overlays: use API roof sections; if AI returned 0 planes, show one full-roof phase so something is highlighted
                              const sectionsFromApi = analysis.roofSections?.filter((plane, planeIdx) => {
                                if (isPrimaryImage) return true;
                                return plane.visibleInImages?.includes(idx) ?? false;
                              }) ?? [];
                              const visiblePlanes = sectionsFromApi.length > 0
                                ? sectionsFromApi
                                : (isPrimaryImage && effectiveAnalysis?.squareFootage
                                    ? [{ name: 'Roof area (full roof)', type: 'roof', area: effectiveAnalysis.squareFootage, pitch: effectiveAnalysis.pitch ?? 0, pitchRatio: effectiveAnalysis.pitchRatio ?? '', bounds: { x: 12, y: 12, width: 76, height: 76 }, visibleInImages: [primaryImageIndex] } as RoofSection]
                                    : []);

                              return (
                                <div key={idx} className="relative">
                                  <div className="relative w-full aspect-video rounded-lg overflow-hidden border-2 border-border">
                                    <img 
                                      src={img} 
                                      alt={`Analyzed roof ${idx + 1}`} 
                                      className="w-full h-full object-cover"
                                    />
                                    {/* House-top indicator (like project location pin) — green pin near roof center */}
                                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none flex flex-col items-center">
                                      <MapPin className="h-8 w-8 text-green-600 drop-shadow-lg fill-green-500/90 stroke-white stroke-[2.5] flex-shrink-0" aria-hidden />
                                      <span className="text-[10px] font-semibold text-white drop-shadow-md bg-green-600/90 px-1.5 py-0.5 rounded mt-0.5 whitespace-nowrap">Property</span>
                                    </div>
                                    {/* Plane Overlays - always show shaded areas + borders for every section; polygon lines on top when available */}
                                    {visiblePlanes.length > 0 && (
                                      <div className="absolute inset-0 pointer-events-none">
                                        {/* Always: colored shaded rectangles with borders for each roof section (so lines are always visible) */}
                                        {visiblePlanes.map((plane, planeIdx) => {
                                          const fullPlaneIndex = analysis.roofSections?.findIndex(p => p.name === plane.name) ?? planeIdx;
                                          const color = planeColors[fullPlaneIndex % planeColors.length];
                                          const planeLabel = String.fromCharCode(65 + fullPlaneIndex);
                                          const strokeHex = color === "blue" ? "#3b82f6" : color === "green" ? "#22c55e" : color === "purple" ? "#a855f7" : color === "orange" ? "#f97316" : color === "red" ? "#ef4444" : color === "cyan" ? "#06b6d4" : "#ec4899";
                                          const fillRgba = color === "blue" ? "rgba(59, 130, 246, 0.4)" : color === "green" ? "rgba(34, 197, 94, 0.4)" : color === "purple" ? "rgba(168, 85, 247, 0.4)" : color === "orange" ? "rgba(249, 115, 22, 0.4)" : color === "red" ? "rgba(239, 68, 68, 0.4)" : color === "cyan" ? "rgba(6, 182, 212, 0.4)" : "rgba(236, 72, 153, 0.4)";
                                          let bounds: { x: number; y: number; width: number; height: number };
                                          const b = plane.bounds;
                                          const hasValidBounds = b && typeof b.x === 'number' && typeof b.y === 'number' && typeof b.width === 'number' && typeof b.height === 'number' && b.width > 0 && b.height > 0;
                                          if (isPrimaryImage && hasValidBounds) {
                                            bounds = { x: b!.x, y: b!.y, width: b!.width, height: b!.height };
                                          } else if (isPrimaryImage && plane.polygon && plane.polygon.length >= 3) {
                                            const xs = plane.polygon.map(p => Number(p.x));
                                            const ys = plane.polygon.map(p => Number(p.y));
                                            bounds = {
                                              x: Math.min(...xs),
                                              y: Math.min(...ys),
                                              width: Math.max(10, Math.max(...xs) - Math.min(...xs)),
                                              height: Math.max(10, Math.max(...ys) - Math.min(...ys))
                                            };
                                          } else {
                                            const n = analysis.roofSections?.length ?? 1;
                                            const cols = Math.ceil(Math.sqrt(n));
                                            const row = Math.floor(fullPlaneIndex / cols);
                                            const col = fullPlaneIndex % cols;
                                            bounds = {
                                              x: 5 + (col * (90 / cols)),
                                              y: 5 + (row * (90 / Math.ceil(n / cols))),
                                              width: Math.max(15, 85 / cols),
                                              height: Math.max(15, 85 / Math.ceil(n / cols))
                                            };
                                          }
                                          return (
                                            <div
                                              key={planeIdx}
                                              style={{
                                                position: 'absolute',
                                                left: `${Math.max(0, bounds.x)}%`,
                                                top: `${Math.max(0, bounds.y)}%`,
                                                width: `${Math.min(100 - bounds.x, bounds.width)}%`,
                                                height: `${Math.min(100 - bounds.y, bounds.height)}%`,
                                                backgroundColor: fillRgba,
                                                border: `2px solid ${strokeHex}`,
                                                borderRadius: '4px',
                                              }}
                                            >
                                              <div className="absolute -top-6 left-0 bg-black/80 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1 z-10 whitespace-nowrap">
                                                <span className={`w-3 h-3 rounded shrink-0 ${color === "blue" ? "bg-blue-500" : color === "green" ? "bg-green-500" : color === "purple" ? "bg-purple-500" : color === "orange" ? "bg-orange-500" : color === "red" ? "bg-red-500" : color === "cyan" ? "bg-cyan-500" : "bg-pink-500"}`} />
                                                {planeLabel}: {safeLocale(plane.area)} sq ft
                                              </div>
                                            </div>
                                          );
                                        })}
                                        {/* When polygon data exists: draw precise roof lines on top */}
                                        {isPrimaryImage && visiblePlanes.some(p => p.polygon && p.polygon.length >= 3) && (
                                          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                                            {visiblePlanes.map((plane, planeIdx) => {
                                              if (!plane.polygon || plane.polygon.length < 3) return null;
                                              const fullPlaneIndex = analysis.roofSections?.findIndex(p => p.name === plane.name) ?? planeIdx;
                                              const color = planeColors[fullPlaneIndex % planeColors.length];
                                              const stroke = color === "blue" ? "#1d4ed8" : color === "green" ? "#15803d" : color === "purple" ? "#7c3aed" : color === "orange" ? "#ea580c" : color === "red" ? "#dc2626" : color === "cyan" ? "#0891b2" : "#be185d";
                                              const points = plane.polygon.map(p => `${Number(p.x)},${Number(p.y)}`).join(' ');
                                              return <polygon key={planeIdx} points={points} fill="none" stroke={stroke} strokeWidth={1.2} />;
                                            })}
                                          </svg>
                                        )}
                                      </div>
                                    )}
                                    {/* Image indicator badges */}
                                    <div className="absolute top-2 right-2 flex gap-1">
                                      {isPrimaryImage && (
                                        <Badge className="bg-blue-600 text-white text-xs">
                                          Top View
                                        </Badge>
                                      )}
                                      {visiblePlanes.length > 0 && !isPrimaryImage && (
                                        <Badge variant="outline" className="text-xs">
                                          {visiblePlanes.length} plane{visiblePlanes.length !== 1 ? 's' : ''}
                                        </Badge>
                                      )}
                                    </div>
                                    {isPrimaryImage && (
                                      <div className="absolute bottom-2 left-2 bg-black/70 text-white px-3 py-1 rounded-lg backdrop-blur-sm">
                                        <div className="text-xs font-medium capitalize">{analysis.roofType} roof</div>
                                        {(analysis as any)?.measurementProvenance?.provider?.toLowerCase?.().includes('solar') || (analysis as any)?.imageryQuality ? (
                                          <div className="text-xs text-gray-300">Imagery: {imageryLabel((analysis as any)?.imageryQuality)}</div>
                                        ) : (
                                          <div className="text-xs text-gray-300">Confidence: {confidenceLabel(analysis.confidence)}</div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {analysis.roofSections && analysis.roofSections.length > 0 && (
                            <div className="mt-4 p-3 bg-muted rounded-lg">
                              <p className="text-xs text-muted-foreground">
                                <strong>Legend:</strong> Colored overlays show detected roof planes across all images. Each plane is consistently labeled (A, B, C...) and appears on images where it's visible. The "Top View" badge indicates the primary aerial image used for plane identification. Colors match the Roof Planes Breakdown diagram.
                              </p>
                            </div>
                          )}
                          {analysis.roofSections && analysis.roofSections.length <= 1 && (
                            <div className="mt-4 p-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40">
                              <p className="text-sm text-amber-800 dark:text-amber-200">
                                {analysis.roofSections.length === 0 ? (
                                  <><strong>Tip:</strong> The AI returned 0 roof planes. The full roof is highlighted above so you still see a marked area. To see <em>each facet</em> in a different color (main sections, hip ends), try re-analyzing with a clear top-down/aerial photo.</>
                                ) : (
                                  <><strong>Tip:</strong> The AI returned 1 roof plane, so the full roof is highlighted. To see each facet in a different color, we need multiple planes; try re-analyzing with a clear top-down/aerial photo.</>
                                )}
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Roof diagram - precise polygon lines and area labels (like reference) */}
                    {analysis.roofSections && analysis.roofSections.length > 0 && analysis.roofSections.some(s => s.polygon && s.polygon.length >= 3) && uploadedImages.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Roof diagram</CardTitle>
                          <CardDescription>All roof sections with precise polygon outlines and area per section</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="relative w-full max-w-3xl mx-auto rounded-lg overflow-hidden border border-border bg-white">
                            {/* Optional: primary image as faint background */}
                            {uploadedImages[analysis.primaryImageIndex ?? 0] && (
                              <>
                                <img
                                  src={uploadedImages[analysis.primaryImageIndex ?? 0]}
                                  alt="Roof reference"
                                  className="absolute inset-0 w-full h-full object-cover opacity-20"
                                  aria-hidden
                                />
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none flex flex-col items-center">
                                  <MapPin className="h-8 w-8 text-green-600 drop-shadow-lg fill-green-500/90 stroke-white stroke-[2.5] flex-shrink-0" aria-hidden />
                                  <span className="text-[10px] font-semibold text-white drop-shadow-md bg-green-600/90 px-1.5 py-0.5 rounded mt-0.5 whitespace-nowrap">Property</span>
                                </div>
                              </>
                            )}
                            <svg className="relative w-full aspect-video" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
                              {analysis.roofSections.map((section, idx) => {
                                if (!section.polygon || section.polygon.length < 3) return null;
                                const points = section.polygon.map(p => `${p.x},${p.y}`).join(' ');
                                const cx = section.polygon.reduce((s, p) => s + p.x, 0) / section.polygon.length;
                                const cy = section.polygon.reduce((s, p) => s + p.y, 0) / section.polygon.length;
                                const areaText = `${typeof section.area === 'number' ? section.area.toFixed(1) : section.area} ft²`;
                                return (
                                  <g key={idx}>
                                    <polygon points={points} fill="rgba(0,0,0,0.08)" stroke="currentColor" strokeWidth={0.6} className="text-foreground" />
                                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" className="fill-foreground text-[2.5px] font-semibold" style={{ fontSize: '2.5px' }}>{areaText}</text>
                                  </g>
                                );
                              })}
                              {/* Total Roof Area box */}
                              <g>
                                <rect x={78} y={88} width={20} height={10} rx={1} fill="transparent" stroke="currentColor" strokeWidth={0.5} className="text-foreground" />
                                <text x={88} y={93.2} textAnchor="middle" dominantBaseline="middle" className="fill-foreground font-bold" style={{ fontSize: '3px' }}>Total Roof Area</text>
                                <text x={88} y={95.8} textAnchor="middle" dominantBaseline="middle" className="fill-foreground font-bold" style={{ fontSize: '4px' }}>{(effectiveAnalysis.squareFootage ?? 0).toLocaleString()} ft²</text>
                              </g>
                            </svg>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Manual Adjustments */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Adjust Measurements / Features (Optional)</CardTitle>
                        <CardDescription>
                          If the AI over-calculated, correct values here. The Estimate + PDF will use your adjusted numbers.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>Total Roof Area (sq ft)</Label>
                          <Input
                            type="number"
                            value={manualOverrides.squareFootage ?? effectiveAnalysis.squareFootage ?? ''}
                            onChange={(e) => {
                              const v = e.target.value === '' ? undefined : Number(e.target.value);
                              setManualOverrides(prev => ({ ...prev, squareFootage: (typeof v === 'number' && Number.isFinite(v)) ? v : undefined }));
                              const updated = getEffectiveAnalysis();
                              if (updated) calculatePricing(updated, wastePercent, regionalLaborRate);
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Ridge Length (ft)</Label>
                          <Input
                            type="number"
                            value={manualOverrides.ridgeLength ?? effectiveAnalysis.ridgeLength ?? ''}
                            onChange={(e) => {
                              const v = e.target.value === '' ? undefined : Number(e.target.value);
                              setManualOverrides(prev => ({ ...prev, ridgeLength: (typeof v === 'number' && Number.isFinite(v)) ? v : undefined }));
                              const updated = getEffectiveAnalysis();
                              if (updated) calculatePricing(updated, wastePercent, regionalLaborRate);
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Eave Length (ft)</Label>
                          <Input
                            type="number"
                            value={manualOverrides.eaveLength ?? effectiveAnalysis.eaveLength ?? ''}
                            onChange={(e) => {
                              const v = e.target.value === '' ? undefined : Number(e.target.value);
                              setManualOverrides(prev => ({ ...prev, eaveLength: (typeof v === 'number' && Number.isFinite(v)) ? v : undefined }));
                              const updated = getEffectiveAnalysis();
                              if (updated) calculatePricing(updated, wastePercent, regionalLaborRate);
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Valley Length (ft)</Label>
                          <Input
                            type="number"
                            value={manualOverrides.valleyLength ?? effectiveAnalysis.valleyLength ?? ''}
                            onChange={(e) => {
                              const v = e.target.value === '' ? undefined : Number(e.target.value);
                              setManualOverrides(prev => ({ ...prev, valleyLength: (typeof v === 'number' && Number.isFinite(v)) ? v : undefined }));
                              const updated = getEffectiveAnalysis();
                              if (updated) calculatePricing(updated, wastePercent, regionalLaborRate);
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Vents</Label>
                          <Input
                            type="number"
                            value={manualOverrides.vents ?? effectiveAnalysis.features?.vents ?? 0}
                            onChange={(e) => {
                              const v = e.target.value === '' ? undefined : Number(e.target.value);
                              setManualOverrides(prev => ({ ...prev, vents: (typeof v === 'number' && Number.isFinite(v)) ? v : undefined }));
                              const updated = getEffectiveAnalysis();
                              if (updated) calculatePricing(updated, wastePercent, regionalLaborRate);
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Chimneys</Label>
                          <Input
                            type="number"
                            value={manualOverrides.chimneys ?? effectiveAnalysis.features?.chimneys ?? 0}
                            onChange={(e) => {
                              const v = e.target.value === '' ? undefined : Number(e.target.value);
                              setManualOverrides(prev => ({ ...prev, chimneys: (typeof v === 'number' && Number.isFinite(v)) ? v : undefined }));
                              const updated = getEffectiveAnalysis();
                              if (updated) calculatePricing(updated, wastePercent, regionalLaborRate);
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Skylights</Label>
                          <Input
                            type="number"
                            value={manualOverrides.skylights ?? effectiveAnalysis.features?.skylights ?? 0}
                            onChange={(e) => {
                              const v = e.target.value === '' ? undefined : Number(e.target.value);
                              setManualOverrides(prev => ({ ...prev, skylights: (typeof v === 'number' && Number.isFinite(v)) ? v : undefined }));
                              const updated = getEffectiveAnalysis();
                              if (updated) calculatePricing(updated, wastePercent, regionalLaborRate);
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Dormers</Label>
                          <Input
                            type="number"
                            value={manualOverrides.dormers ?? effectiveAnalysis.features?.dormers ?? 0}
                            onChange={(e) => {
                              const v = e.target.value === '' ? undefined : Number(e.target.value);
                              setManualOverrides(prev => ({ ...prev, dormers: (typeof v === 'number' && Number.isFinite(v)) ? v : undefined }));
                              const updated = getEffectiveAnalysis();
                              if (updated) calculatePricing(updated, wastePercent, regionalLaborRate);
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Valleys (count)</Label>
                          <Input
                            type="number"
                            value={manualOverrides.valleys ?? effectiveAnalysis.features?.valleys ?? 0}
                            onChange={(e) => {
                              const v = e.target.value === '' ? undefined : Number(e.target.value);
                              setManualOverrides(prev => ({ ...prev, valleys: (typeof v === 'number' && Number.isFinite(v)) ? v : undefined }));
                              const updated = getEffectiveAnalysis();
                              if (updated) calculatePricing(updated, wastePercent, regionalLaborRate);
                            }}
                            className="mt-1"
                          />
                        </div>
                      </CardContent>
                    </Card>

                    {/* Top Cards Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Roof Overview */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Home className="h-4 w-4" />
                            Roof Overview
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div>
                            <div className="text-xs text-muted-foreground">Roof Type</div>
                            <div className="text-lg font-bold capitalize">{effectiveAnalysis.roofType}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Pitch</div>
                            <div className="text-lg font-bold">{effectiveAnalysis.pitchRatio || `${safeNum(effectiveAnalysis.pitch)}°`}</div>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Complexity</div>
                            <Badge variant="outline" className="mt-1 capitalize">{effectiveAnalysis.complexity}</Badge>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground">Existing Layers</div>
                            <div className="text-lg font-bold">{safeNum(effectiveAnalysis.existingLayers, 1) || 1}</div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Confidence */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <TrendingUp className="h-4 w-4" />
                            Confidence
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {((effectiveAnalysis as any)?.measurementProvenance?.provider?.toLowerCase?.().includes('solar') || (effectiveAnalysis as any)?.imageryQuality) ? (
                            <>
                              <div className="flex items-center gap-2">
                                <div className="text-3xl font-bold">{imageryLabel((effectiveAnalysis as any)?.imageryQuality)}</div>
                                <Zap className="h-5 w-5 text-yellow-500" />
                              </div>
                              <div className="text-xs text-muted-foreground">
                                Aerial imagery quality (Google Solar). No percentage is shown.
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-2">
                                <div className="text-3xl font-bold">{confidenceLabel(effectiveAnalysis.confidence)}</div>
                                <Zap className="h-5 w-5 text-yellow-500" />
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {confidenceLabel(effectiveAnalysis.confidence) === 'High'
                                  ? 'High confidence — accurate estimate'
                                  : confidenceLabel(effectiveAnalysis.confidence) === 'Moderate'
                                    ? 'Moderate confidence — review recommended'
                                    : 'Low confidence — manual verification needed'}
                              </div>
                            </>
                          )}
                        </CardContent>
                      </Card>

                      {/* Measurements */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Ruler className="h-4 w-4" />
                            Measurements
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div>
                            <div className="text-xs text-muted-foreground">Total Area</div>
                            <div className="text-lg font-bold">{safeLocale(effectiveAnalysis.squareFootage)} sq ft</div>
                          </div>
                          {(() => {
                            const sq = safeNum(effectiveAnalysis.squareFootage);
                            const ridge = safeNum(effectiveAnalysis.ridgeLength);
                            const eave = safeNum(effectiveAnalysis.eaveLength);
                            const valley = safeNum(effectiveAnalysis.valleyLength);
                            const ridgeDisplay = ridge > 0 ? `${safeLocale(ridge)} linear ft` : sq > 0 ? `~${Math.round(Math.sqrt(sq)).toLocaleString()} ft (est. from area)` : '—';
                            const eaveDisplay = eave > 0 ? `${safeLocale(eave)} linear ft` : sq > 0 ? `~${Math.round(4 * Math.sqrt(sq)).toLocaleString()} ft (est. from area)` : '—';
                            const valleyDisplay = valley > 0 ? `${safeLocale(valley)} linear ft` : sq > 0 ? '0 linear ft' : '—';
                            return (
                              <>
                                <div>
                                  <div className="text-xs text-muted-foreground">Ridge Length</div>
                                  <div className="text-lg font-bold">{ridgeDisplay}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-muted-foreground">Eave Length</div>
                                  <div className="text-lg font-bold">{eaveDisplay}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-muted-foreground">Valley Length</div>
                                  <div className="text-lg font-bold">{valleyDisplay}</div>
                                </div>
                              </>
                            );
                          })()}
                        </CardContent>
                      </Card>

                    </div>

                    {/* Roof Sections — AI only (no manual in Analysis) */}
                    {analysis.roofSections && analysis.roofSections.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Roof Sections</CardTitle>
                          <CardDescription>Per-plane area and pitch from AI analysis.</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {analysis.roofSections.map((section, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                                <div>
                                  <div className="font-medium">{section.name}</div>
                                  <div className="text-sm text-muted-foreground">
                                    Type: {section.type} • Area: {section.area.toLocaleString()} sq ft • Pitch: {section.pitchRatio || `${section.pitch}°`}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Detected Features */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Layers className="h-5 w-5" />
                          Detected Features
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {[
                            { name: 'Chimney', count: effectiveAnalysis.features?.chimneys ?? 0 },
                            { name: 'Skylights', count: effectiveAnalysis.features?.skylights ?? 0 },
                            { name: 'Vents', count: effectiveAnalysis.features?.vents ?? 0 },
                            { name: 'Dormers', count: effectiveAnalysis.features?.dormers ?? 0 },
                            { name: 'Valleys', count: effectiveAnalysis.features?.valleys ?? 0 },
                            { name: 'Hips', count: effectiveAnalysis.features?.hips ?? 0 },
                            { name: 'Ridges', count: effectiveAnalysis.features?.ridges ?? 0 }
                          ].map((feature) => (
                            <div key={feature.name} className="flex items-center justify-between p-2 border rounded-lg">
                              <span className="text-sm">{feature.name}</span>
                              <Badge variant={feature.count > 0 ? "default" : "outline"}>
                                {safeNum(feature.count) > 0 ? <CheckCircle className="h-3 w-3 mr-1" /> : null}
                                {safeNum(feature.count)}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Condition Assessment */}
                    {analysis.conditionDetails && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Wind className="h-5 w-5" />
                            Condition Assessment
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="capitalize">{analysis.conditionDetails.overallStatus}</Badge>
                            <span className="text-sm text-muted-foreground">
                              Urgency: <span className="capitalize">{analysis.conditionDetails.urgency}</span>
                            </span>
                          </div>

                          {analysis.conditionDetails.detectedIssues && analysis.conditionDetails.detectedIssues.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="h-4 w-4 text-orange-500" />
                                <h4 className="font-semibold">Detected Issues</h4>
                              </div>
                              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-6">
                                {analysis.conditionDetails.detectedIssues.map((issue, idx) => (
                                  <li key={idx}>{issue}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {analysis.conditionDetails.recommendations && analysis.conditionDetails.recommendations.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                <h4 className="font-semibold">Recommendations</h4>
                              </div>
                              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-6">
                                {analysis.conditionDetails.recommendations.map((rec, idx) => (
                                  <li key={idx}>{rec}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {analysis.conditionDetails.aiObservations && analysis.conditionDetails.aiObservations.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Info className="h-4 w-4 text-blue-500" />
                                <h4 className="font-semibold">AI Observations</h4>
                              </div>
                              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-6">
                                {analysis.conditionDetails.aiObservations.map((obs, idx) => (
                                  <li key={idx}>{obs}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                  );
                })() : (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <p className="text-[var(--dash-text-muted)]">Enter a property address above and click "Get roof report" to see results.</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Diagram Tab */}
              <TabsContent value="diagram" className="space-y-6">
                {analysis ? (() => {
                  const effectiveAnalysis = getEffectiveAnalysis() || analysis;
                  const safeNum = (v: any, fallback = 0) =>
                    (typeof v === "number" && Number.isFinite(v)) ? v : (Number.isFinite(Number(v)) ? Number(v) : fallback);
                  const safeLocale = (v: any) => safeNum(v, 0).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

                  // Single source: use manual data for Diagram when user has drawn lines/planes; otherwise AI only
                  const useManualForDiagram = manualLines.length > 0 || manualPolygons.length > 0;

                  // Line type → stroke color on diagram (ridge, hip, valley, eave, rake)
                  const lineTypeStroke: Record<string, string> = { ridge: "#3b82f6", hip: "#f97316", valley: "#ef4444", eave: "#22c55e", rake: "#a855f7" };

                  const roofPlanes = effectiveAnalysis.roofSections || [];
                  const structuralLines = effectiveAnalysis.structuralLines ?? {};
                  const hipLines: StructuralLineSegment[] = structuralLines.hipLines ?? [];
                  const ridgeLines: StructuralLineSegment[] = structuralLines.ridgeLines ?? [];
                  const valleyLines: StructuralLineSegment[] = structuralLines.valleyLines ?? [];
                  const eaveLines: StructuralLineSegment[] = structuralLines.eaveLines ?? [];
                  const rakeLines: StructuralLineSegment[] = structuralLines.rakeLines ?? [];
                  const allStructuralLines: Array<StructuralLineSegment & { key: string; lineType: string }> = [
                    ...hipLines.map((l, i) => ({ ...l, key: `hip-${i}`, lineType: 'hip' })),
                    ...ridgeLines.map((l, i) => ({ ...l, key: `ridge-${i}`, lineType: 'ridge' })),
                    ...valleyLines.map((l, i) => ({ ...l, key: `valley-${i}`, lineType: 'valley' })),
                    ...eaveLines.map((l, i) => ({ ...l, key: `eave-${i}`, lineType: 'eave' })),
                    ...rakeLines.map((l, i) => ({ ...l, key: `rake-${i}`, lineType: 'rake' }))
                  ];

                  // Length measurements: from manual lines (by type) when useManualForDiagram, else from AI
                  const lengthMeasurements = useManualForDiagram
                    ? (() => {
                        const byType: Record<string, number> = { ridge: 0, eave: 0, rake: 0, hip: 0, valley: 0 };
                        manualLines.forEach((l) => {
                          const len = manualLineLengthFt(l);
                          if (len != null) byType[l.type] = (byType[l.type] ?? 0) + len;
                        });
                        const total = byType.ridge + byType.eave + byType.rake + byType.hip + byType.valley;
                        return [
                          { label: "Ridge Length", value: byType.ridge, description: "Top edges where planes meet", color: "blue" },
                          { label: "Eave Length", value: byType.eave, description: "Horizontal lower edges", color: "green" },
                          { label: "Rake Length", value: byType.rake, description: "Sloped gable edges", color: "purple" },
                          { label: "Hip Length", value: byType.hip, description: "Outward sloping edges", color: "orange" },
                          { label: "Valley Length", value: byType.valley, description: "Inward sloping edges", color: "red" },
                          { label: "Total (manual)", value: total, description: "Sum of marked lines", color: "gray" }
                        ];
                      })()
                    : [
                        { label: "Ridge Length", value: safeNum(effectiveAnalysis.ridgeLength), description: "Top edges where planes meet", color: "blue" },
                        { label: "Eave Length", value: safeNum(effectiveAnalysis.eaveLength), description: "Horizontal lower edges", color: "green" },
                        { label: "Rake Length", value: safeNum(effectiveAnalysis.rakeLength), description: "Sloped gable edges", color: "purple" },
                        { label: "Hip Length", value: safeNum(effectiveAnalysis.hipLength), description: "Outward sloping edges", color: "orange" },
                        { label: "Valley Length", value: safeNum(effectiveAnalysis.valleyLength), description: "Inward sloping edges", color: "red" },
                        { label: "Step Flashing", value: safeNum(effectiveAnalysis.stepFlashing), description: "Along walls/dormers", color: "cyan" },
                        { label: "Apron Flashing", value: safeNum(effectiveAnalysis.apronFlashing), description: "At wall intersections", color: "pink" },
                        { label: "Total Perimeter", value: safeNum(effectiveAnalysis.ridgeLength) + safeNum(effectiveAnalysis.eaveLength) + safeNum(effectiveAnalysis.rakeLength) + safeNum(effectiveAnalysis.hipLength), description: "Drip edge coverage", color: "gray" }
                      ];

                  const planeColors = ["blue", "green", "purple", "orange", "red", "cyan", "pink"];
                  const planeFillRgba = ["rgba(59,130,246,0.25)", "rgba(34,197,94,0.25)", "rgba(168,85,247,0.25)", "rgba(249,115,22,0.25)", "rgba(239,68,68,0.25)", "rgba(6,182,212,0.25)", "rgba(236,72,153,0.25)"];
                  const primaryIdx = effectiveAnalysis.primaryImageIndex ?? 0;
                  const primaryImageUrl = uploadedImages[primaryIdx];
                  
                  return (
                    <div className="space-y-6">
                      {/* Satellite sky view (aerial imagery, when available) */}
                      {effectiveAnalysis.source === 'google_solar' && effectiveAnalysis.satelliteImageDataUrl && (
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <ImageIcon className="h-5 w-5" />
                              Aerial Roof View
                            </CardTitle>
                            <CardDescription>
                              Static aerial view centered on the property. For visual context only; all measurements in this report are generated by Remodra.
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="relative w-full max-w-3xl mx-auto">
                              <img
                                src={effectiveAnalysis.satelliteImageDataUrl}
                                alt="Satellite roof view"
                                className="w-full rounded-lg border bg-muted object-cover"
                              />
                              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none flex flex-col items-center">
                                <MapPin className="h-8 w-8 text-green-600 drop-shadow-lg fill-green-500/90 stroke-white stroke-[2.5] flex-shrink-0" aria-hidden />
                                <span className="text-[10px] font-semibold text-white drop-shadow-md bg-green-600/90 px-1.5 py-0.5 rounded mt-0.5 whitespace-nowrap">Property</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Manual Roof Tracing (Roofr-style) */}
                      {primaryImageUrl && (
                        <Card>
                          <CardHeader>
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <CardTitle className="flex items-center gap-2">
                                  <Ruler className="h-5 w-5" />
                                  Manual Roof Tracing (Roofr-style)
                                </CardTitle>
                                <CardDescription>
                                  Click to trace roof planes and/or calibrate one known length. This is the “manual lines → AI math” path when automatic AI planes are wrong.
                                </CardDescription>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  variant={isManualMode ? "default" : "outline"}
                                  onClick={() => {
                                    setIsManualMode(v => !v);
                                    setManualToolMode('polygon');
                                  }}
                                >
                                  {isManualMode ? "Manual mode: ON" : "Manual mode: OFF"}
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div className="flex flex-wrap items-end gap-3">
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant={manualToolMode === 'polygon' ? "default" : "outline"}
                                  disabled={!isManualMode}
                                  onClick={() => setManualToolMode('polygon')}
                                >
                                  Draw plane
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant={manualToolMode === 'line' ? "default" : "outline"}
                                  disabled={!isManualMode}
                                  onClick={() => {
                                    setManualToolMode('line');
                                    setManualLineP1(null);
                                  }}
                                >
                                  Draw lines
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant={manualToolMode === 'calibrate' ? "default" : "outline"}
                                  disabled={!isManualMode}
                                  onClick={() => {
                                    setManualToolMode('calibrate');
                                    setManualCalibP1(null);
                                    setManualCalibP2(null);
                                  }}
                                >
                                  Calibrate
                                </Button>
                              </div>
                              <div className="min-w-[220px]">
                                <Label>Plane name</Label>
                                <Input
                                  value={manualPolygonName}
                                  onChange={(e) => setManualPolygonName(e.target.value)}
                                  className="mt-1"
                                  disabled={!isManualMode || manualToolMode !== 'polygon'}
                                />
                              </div>
                              <div className="min-w-[220px]">
                                <Label>Line type</Label>
                                <Select
                                  value={manualLineType}
                                  onValueChange={(v) => setManualLineType(v as any)}
                                  disabled={!isManualMode || manualToolMode !== 'line'}
                                >
                                  <SelectTrigger className="mt-1">
                                    <SelectValue placeholder="Select line type" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="hip">Hip</SelectItem>
                                    <SelectItem value="ridge">Ridge</SelectItem>
                                    <SelectItem value="valley">Valley</SelectItem>
                                    <SelectItem value="eave">Eave</SelectItem>
                                    <SelectItem value="rake">Rake</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="min-w-[200px]">
                                <Label>Calibration length (ft)</Label>
                                <Input
                                  type="number"
                                  value={manualCalibLengthFt || ''}
                                  onChange={(e) => setManualCalibLengthFt(Number(e.target.value) || 0)}
                                  className="mt-1"
                                  disabled={!isManualMode || manualToolMode !== 'calibrate'}
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={!isManualMode || manualToolMode !== 'polygon' || manualCurrentPoints.length === 0}
                                  onClick={() => setManualCurrentPoints(prev => prev.slice(0, -1))}
                                >
                                  Undo point
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  disabled={!isManualMode || manualToolMode !== 'polygon' || manualCurrentPoints.length < 3}
                                  onClick={() => {
                                    setManualPolygons(prev => [...prev, { name: manualPolygonName.trim() || `Plane ${prev.length + 1}`, points: manualCurrentPoints }]);
                                    setManualCurrentPoints([]);
                                    setManualPolygonName((p) => {
                                      const m = p.match(/Plane\s+(\d+)/i);
                                      const next = m ? Number(m[1]) + 1 : (manualPolygons.length + 2);
                                      return `Plane ${next}`;
                                    });
                                  }}
                                >
                                  Close plane
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  disabled={!isManualMode || (manualPolygons.length === 0 && manualLines.length === 0 && manualCurrentPoints.length === 0 && !manualCalibP1 && !manualCalibP2)}
                                  onClick={() => {
                                    setManualCurrentPoints([]);
                                    setManualPolygons([]);
                                    setManualLines([]);
                                    setManualLineP1(null);
                                    setManualCalibP1(null);
                                    setManualCalibP2(null);
                                    setManualCalibLengthFt(0);
                                  }}
                                >
                                  Clear all
                                </Button>
                              </div>
                            </div>

                            <div className="text-sm text-muted-foreground">
                              {manualToolMode === 'polygon' ? (
                                <div>
                                  <strong>Draw plane:</strong> click along the roof edges of ONE facet. Add 3+ points, then click “Close plane”.
                                </div>
                              ) : manualToolMode === 'line' ? (
                                <div>
                                  <strong>Draw lines:</strong> click two points to create one line segment. Choose the type (hip/ridge/valley/eave/rake). Repeat for each roof edge you want measured.
                                </div>
                              ) : (
                                <div>
                                  <strong>Calibrate:</strong> click two points on a known-length edge (e.g. a ridge segment), then enter the real length in feet. This enables sq ft math.
                                </div>
                              )}
                            </div>

                            <div
                              ref={manualDiagramRef}
                              className={`relative w-full max-w-5xl mx-auto rounded-lg overflow-hidden border border-border bg-black ${isManualMode ? 'cursor-crosshair' : ''}`}
                              onClick={(e) => {
                                if (!isManualMode) return;
                                const p = getClickPct(e);
                                if (!p) return;
                                if (manualToolMode === 'polygon') {
                                  setManualCurrentPoints(prev => [...prev, p]);
                                } else if (manualToolMode === 'line') {
                                  if (!manualLineP1) setManualLineP1(p);
                                  else {
                                    const p1 = manualLineP1;
                                    setManualLines(prev => [...prev, { type: manualLineType, p1, p2: p }]);
                                    setManualLineP1(null);
                                  }
                                } else {
                                  if (!manualCalibP1) setManualCalibP1(p);
                                  else if (!manualCalibP2) setManualCalibP2(p);
                                  else {
                                    setManualCalibP1(p);
                                    setManualCalibP2(null);
                                  }
                                }
                              }}
                            >
                              <img
                                src={primaryImageUrl}
                                alt="Manual tracing base"
                                className="absolute inset-0 w-full h-full object-fill"
                              />
                              <svg className="relative w-full aspect-video" viewBox="0 0 100 100" preserveAspectRatio="none">
                                {/* Existing saved manual planes */}
                                {manualPolygons.map((poly, idx) => {
                                  const pts = poly.points.map(p => `${p.x},${p.y}`).join(' ');
                                  const fill = planeFillRgba[idx % planeFillRgba.length].replace('0.25', '0.18');
                                  return (
                                    <g key={`m-poly-${idx}`}>
                                      <polygon points={pts} fill={fill} stroke="#ea580c" strokeWidth={0.65} />
                                      {poly.points.map((p, pi) => (
                                        <circle key={`m-poly-${idx}-pt-${pi}`} cx={p.x} cy={p.y} r={0.45} fill="#ea580c" />
                                      ))}
                                    </g>
                                  );
                                })}

                                {/* Current drawing plane */}
                                {manualCurrentPoints.length > 0 && (
                                  <g>
                                    <polyline
                                      points={manualCurrentPoints.map(p => `${p.x},${p.y}`).join(' ')}
                                      fill="none"
                                      stroke="#22c55e"
                                      strokeWidth={0.65}
                                    />
                                    {manualCurrentPoints.map((p, idx) => (
                                      <circle key={`m-cur-${idx}`} cx={p.x} cy={p.y} r={0.5} fill="#22c55e" />
                                    ))}
                                  </g>
                                )}

                                {/* Calibration line */}
                                {manualCalibP1 && (
                                  <circle cx={manualCalibP1.x} cy={manualCalibP1.y} r={0.6} fill="#60a5fa" />
                                )}
                                {manualCalibP2 && (
                                  <circle cx={manualCalibP2.x} cy={manualCalibP2.y} r={0.6} fill="#60a5fa" />
                                )}
                                {manualCalibP1 && manualCalibP2 && (
                                  <line x1={manualCalibP1.x} y1={manualCalibP1.y} x2={manualCalibP2.x} y2={manualCalibP2.y} stroke="#60a5fa" strokeWidth={0.9} strokeLinecap="round" />
                                )}

                                {/* Manual roof lines (typed segments) */}
                                {manualLines.map((l, idx) => (
                                  <line
                                    key={`m-line-${idx}`}
                                    x1={l.p1.x}
                                    y1={l.p1.y}
                                    x2={l.p2.x}
                                    y2={l.p2.y}
                                    stroke="#ea580c"
                                    strokeWidth={0.75}
                                    strokeLinecap="round"
                                  />
                                ))}
                                {manualLineP1 && (
                                  <circle cx={manualLineP1.x} cy={manualLineP1.y} r={0.5} fill="#ea580c" />
                                )}
                              </svg>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="rounded-lg border p-4">
                                <div className="font-semibold mb-2">Manual planes</div>
                                {manualPolygons.length === 0 ? (
                                  <div className="text-sm text-muted-foreground">No planes traced yet.</div>
                                ) : (
                                  <div className="space-y-2">
                                    {manualPolygons.map((p, idx) => {
                                      const areaPct2 = polygonAreaPct2(p.points);
                                      const areaSqFt = feetPerPct ? (areaPct2 * feetPerPct * feetPerPct) : null;
                                      return (
                                        <div key={idx} className="flex items-center justify-between gap-3 p-2 rounded border">
                                          <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">{p.name}</div>
                                            <div className="text-xs text-muted-foreground">
                                              {areaSqFt ? `${areaSqFt.toFixed(1)} sq ft` : `${areaPct2.toFixed(1)} %² (calibrate to get sq ft)`}
                                            </div>
                                          </div>
                                          <Button
                                            type="button"
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => setManualPolygons(prev => prev.filter((_, i) => i !== idx))}
                                          >
                                            Remove
                                          </Button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                              <div className="rounded-lg border p-4 space-y-3">
                                <div className="font-semibold">Manual totals</div>
                                <div className="text-sm text-muted-foreground">
                                  {feetPerPct ? (
                                    <div>
                                      Calibration enabled. Scale: <strong>{feetPerPct.toFixed(3)}</strong> ft per 1% (based on your reference line).
                                    </div>
                                  ) : (
                                    <div>
                                      Calibrate to unlock sq ft math. (Without calibration we can still draw lines/planes.)
                                    </div>
                                  )}
                                </div>
                                <div className="text-lg font-bold">
                                  {manualTotalSqFt ? `${manualTotalSqFt.toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft` : '—'}
                                </div>
                                {manualTotalSqFt != null && manualTotalSqFt > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    <Button
                                      type="button"
                                      onClick={() => {
                                        const byType: Record<string, number> = { ridge: 0, eave: 0, rake: 0, hip: 0, valley: 0 };
                                        manualLines.forEach((l) => {
                                          const len = manualLineLengthFt(l);
                                          if (len != null) byType[l.type] = (byType[l.type] ?? 0) + len;
                                        });
                                        setManualOverrides(prev => ({
                                          ...prev,
                                          squareFootage: manualTotalSqFt,
                                          ridgeLength: byType.ridge || prev.ridgeLength,
                                          eaveLength: byType.eave || prev.eaveLength,
                                          rakeLength: byType.rake || prev.rakeLength,
                                          hipLength: byType.hip || prev.hipLength,
                                          valleyLength: byType.valley || prev.valleyLength
                                        }));
                                        const updated = getEffectiveAnalysis();
                                        if (updated) {
                                          const withTotal = { ...updated, squareFootage: manualTotalSqFt, squares: manualTotalSqFt / 100, ridgeLength: byType.ridge ?? updated.ridgeLength, eaveLength: byType.eave ?? updated.eaveLength, rakeLength: byType.rake ?? updated.rakeLength, hipLength: byType.hip ?? updated.hipLength, valleyLength: byType.valley ?? updated.valleyLength };
                                          calculatePricing(withTotal, wastePercent, regionalLaborRate);
                                        }
                                        toast({
                                          title: "Recalculated",
                                          description: "Estimate, Waste, and PDF use manual totals."
                                        });
                                      }}
                                    >
                                      Recalculate & apply to estimate
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      onClick={() => {
                                        const byType: Record<string, number> = { ridge: 0, eave: 0, rake: 0, hip: 0, valley: 0 };
                                        manualLines.forEach((l) => {
                                          const len = manualLineLengthFt(l);
                                          if (len != null) byType[l.type] = (byType[l.type] ?? 0) + len;
                                        });
                                        setManualOverrides(prev => ({
                                          ...prev,
                                          squareFootage: manualTotalSqFt,
                                          ridgeLength: byType.ridge || prev.ridgeLength,
                                          eaveLength: byType.eave || prev.eaveLength,
                                          rakeLength: byType.rake || prev.rakeLength,
                                          hipLength: byType.hip || prev.hipLength,
                                          valleyLength: byType.valley || prev.valleyLength
                                        }));
                                        const updated = getEffectiveAnalysis();
                                        if (updated) {
                                          const withTotal = { ...updated, squareFootage: manualTotalSqFt, squares: manualTotalSqFt / 100, ridgeLength: byType.ridge ?? updated.ridgeLength, eaveLength: byType.eave ?? updated.eaveLength, rakeLength: byType.rake ?? updated.rakeLength, hipLength: byType.hip ?? updated.hipLength, valleyLength: byType.valley ?? updated.valleyLength };
                                          calculatePricing(withTotal, wastePercent, regionalLaborRate);
                                        }
                                        toast({
                                          title: "Manual total applied",
                                          description: "Estimate and PDF use manual roof area."
                                        });
                                      }}
                                    >
                                      Use manual total for estimate
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="rounded-lg border p-4">
                              <div className="font-semibold mb-2">Line legend (roof edges)</div>
                              {manualLines.length === 0 ? (
                                <div className="text-sm text-muted-foreground">No lines traced yet.</div>
                              ) : (
                                <div className="space-y-2">
                                  {manualLines.map((l, idx) => {
                                    const len = manualLineLengthFt(l);
                                    return (
                                      <div key={idx} className="flex items-center justify-between gap-3 p-2 rounded border">
                                        <div className="min-w-0">
                                          <div className="text-sm font-medium capitalize">{l.type} line</div>
                                          <div className="text-xs text-muted-foreground">
                                            {len ? `${len.toFixed(1)} ft` : `${distPct(l.p1, l.p2).toFixed(2)} % (calibrate to get ft)`}
                                          </div>
                                        </div>
                                        <Button type="button" size="sm" variant="destructive" onClick={() => setManualLines(prev => prev.filter((_, i) => i !== idx))}>
                                          Remove
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Roof diagram: single source — manual (lines by type color) OR AI (planes + lines by type color) */}
                      {primaryImageUrl && (useManualForDiagram || roofPlanes.length > 0 || allStructuralLines.length > 0) && (
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <Layers className="h-5 w-5" />
                              Roof diagram — {useManualForDiagram ? 'manual marks' : 'hip & edge lines'}
                            </CardTitle>
                            <CardDescription>
                              {useManualForDiagram
                                ? 'Manual lines colored by type: Ridge (blue), Hip (orange), Valley (red), Eave (green), Rake (purple).'
                                : 'Each roof face highlighted; lines colored by type: Ridge, Hip, Valley, Eave, Rake.'}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="relative w-full max-w-4xl mx-auto rounded-lg overflow-hidden border border-border bg-white">
                              <img
                                src={primaryImageUrl}
                                alt="Roof diagram reference"
                                className="absolute inset-0 w-full h-full object-fill opacity-90"
                              />
                              <svg className="relative w-full aspect-video" viewBox="0 0 100 100" preserveAspectRatio="none">
                                {useManualForDiagram ? (
                                  <>
                                    {manualPolygons.map((poly, idx) => {
                                      const pts = poly.points.map(p => `${p.x},${p.y}`).join(' ');
                                      const fill = planeFillRgba[idx % planeFillRgba.length];
                                      return <polygon key={`m-${idx}`} points={pts} fill={fill} stroke="transparent" strokeWidth={0} />;
                                    })}
                                    {manualLines.map((l, idx) => (
                                      <line
                                        key={`m-line-${idx}`}
                                        x1={l.p1.x}
                                        y1={l.p1.y}
                                        x2={l.p2.x}
                                        y2={l.p2.y}
                                        stroke={lineTypeStroke[l.type] ?? '#64748b'}
                                        strokeWidth={1.2}
                                        strokeLinecap="round"
                                      />
                                    ))}
                                  </>
                                ) : (
                                  <>
                                    {roofPlanes.map((section, idx) => {
                                      if (!section.polygon || section.polygon.length < 3) return null;
                                      const points = section.polygon.map(p => `${Number(p.x)},${Number(p.y)}`).join(' ');
                                      return (
                                        <polygon
                                          key={`plane-${idx}`}
                                          points={points}
                                          fill={planeFillRgba[idx % planeFillRgba.length]}
                                          stroke="transparent"
                                          strokeWidth={0}
                                        />
                                      );
                                    })}
                                    {allStructuralLines.map((seg) => {
                                      const x1 = Number(seg.x1);
                                      const y1 = Number(seg.y1);
                                      const x2 = Number(seg.x2);
                                      const y2 = Number(seg.y2);
                                      if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) return null;
                                      const stroke = lineTypeStroke[seg.lineType] ?? '#ea580c';
                                      return (
                                        <line
                                          key={seg.key}
                                          x1={x1}
                                          y1={y1}
                                          x2={x2}
                                          y2={y2}
                                          stroke={stroke}
                                          strokeWidth={1.2}
                                          strokeLinecap="round"
                                        />
                                      );
                                    })}
                                    {allStructuralLines.length === 0 && roofPlanes.map((section, idx) => {
                                      if (!section.polygon || section.polygon.length < 3) return null;
                                      const points = section.polygon.map(p => `${Number(p.x)},${Number(p.y)}`).join(' ');
                                      return <polygon key={`outline-${idx}`} points={points} fill="none" stroke="#ea580c" strokeWidth={1.2} />;
                                    })}
                                  </>
                                )}
                              </svg>
                              <div className="absolute bottom-3 right-3 rounded-lg border-2 border-foreground/20 bg-background/95 px-3 py-2 shadow-lg">
                                <div className="text-xs font-medium text-muted-foreground">Total Roof Area</div>
                                <div className="text-lg font-bold">{(effectiveAnalysis.squareFootage ?? 0).toLocaleString()} ft²</div>
                              </div>
                            </div>
                            {useManualForDiagram ? (
                              manualPolygons.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {manualPolygons.map((p, idx) => {
                                    const areaSqFt = feetPerPct ? polygonAreaPct2(p.points) * feetPerPct * feetPerPct : 0;
                                    return (
                                      <span key={idx} className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
                                        <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: planeFillRgba[idx % planeFillRgba.length].replace('0.25', '1') }} />
                                        {p.name}: {areaSqFt.toFixed(1)} ft²
                                      </span>
                                    );
                                  })}
                                </div>
                              )
                            ) : roofPlanes.some(p => p.polygon && p.polygon.length >= 3) && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {roofPlanes.map((section, idx) => (
                                  <span key={idx} className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
                                    <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: planeFillRgba[idx % planeFillRgba.length].replace('0.25', '1') }} />
                                    {section.name}: {typeof section.area === 'number' ? section.area.toFixed(1) : section.area} ft²
                                  </span>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {/* Roof Images with Plane Overlays — AI only (hidden when using manual) */}
                      {!useManualForDiagram && uploadedImages.length > 0 && roofPlanes.length > 0 && (
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                              <ImageIcon className="h-5 w-5" />
                              Roof Planes Visualization - All Views
                            </CardTitle>
                            <CardDescription>
                              Color-coded overlays showing detected roof planes across all images. Same planes are identified consistently across different views.
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {uploadedImages.map((img, idx) => {
                                const primaryImageIndex = effectiveAnalysis.primaryImageIndex ?? 0;
                                const isPrimaryImage = idx === primaryImageIndex;
                                
                                // Get planes visible in this image
                                const visiblePlanes = roofPlanes.filter((plane, planeIdx) => {
                                  if (isPrimaryImage) return true; // Show all planes on primary image
                                  return plane.visibleInImages?.includes(idx) ?? false;
                                });
                                
                                if (visiblePlanes.length === 0 && !isPrimaryImage) return null;
                                
                                return (
                                  <div key={idx} className="relative">
                                    <div className="relative w-full aspect-video rounded-lg overflow-hidden border-2 border-border bg-black">
                                      <img 
                                        src={img} 
                                        alt={`Roof view ${idx + 1}`} 
                                        className="w-full h-full object-contain"
                                      />
                                      {/* House-top indicator (like project location pin) — green pin near roof center */}
                                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none flex flex-col items-center">
                                        <MapPin className="h-8 w-8 text-green-600 drop-shadow-lg fill-green-500/90 stroke-white stroke-[2.5] flex-shrink-0" aria-hidden />
                                        <span className="text-[10px] font-semibold text-white drop-shadow-md bg-green-600/90 px-1.5 py-0.5 rounded mt-0.5 whitespace-nowrap">Property</span>
                                      </div>
                                      {/* Plane Overlays - always show shaded areas + borders; polygon lines on top when available */}
                                      {visiblePlanes.length > 0 && (
                                        <div className="absolute inset-0 pointer-events-none">
                                          {visiblePlanes.map((plane, planeIdx) => {
                                            const fullPlaneIndex = roofPlanes.findIndex(p => p.name === plane.name);
                                            const color = planeColors[fullPlaneIndex % planeColors.length];
                                            const planeLabel = String.fromCharCode(65 + fullPlaneIndex);
                                            const strokeHex = color === "blue" ? "#3b82f6" : color === "green" ? "#22c55e" : color === "purple" ? "#a855f7" : color === "orange" ? "#f97316" : color === "red" ? "#ef4444" : color === "cyan" ? "#06b6d4" : "#ec4899";
                                            const fillRgba = color === "blue" ? "rgba(59, 130, 246, 0.4)" : color === "green" ? "rgba(34, 197, 94, 0.4)" : color === "purple" ? "rgba(168, 85, 247, 0.4)" : color === "orange" ? "rgba(249, 115, 22, 0.4)" : color === "red" ? "rgba(239, 68, 68, 0.4)" : color === "cyan" ? "rgba(6, 182, 212, 0.4)" : "rgba(236, 72, 153, 0.4)";
                                            const b = plane.bounds;
                                            const hasValidBounds = b && typeof b.x === 'number' && typeof b.y === 'number' && typeof b.width === 'number' && typeof b.height === 'number' && b.width > 0 && b.height > 0;
                                            let bounds: { x: number; y: number; width: number; height: number };
                                            if (isPrimaryImage && hasValidBounds) {
                                              bounds = { x: b!.x, y: b!.y, width: b!.width, height: b!.height };
                                            } else if (isPrimaryImage && plane.polygon && plane.polygon.length >= 3) {
                                              const xs = plane.polygon.map(p => Number(p.x));
                                              const ys = plane.polygon.map(p => Number(p.y));
                                              bounds = { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(10, Math.max(...xs) - Math.min(...xs)), height: Math.max(10, Math.max(...ys) - Math.min(...ys)) };
                                            } else {
                                              const n = roofPlanes.length;
                                              const cols = Math.ceil(Math.sqrt(n));
                                              const row = Math.floor(fullPlaneIndex / cols);
                                              const col = fullPlaneIndex % cols;
                                              bounds = { x: 5 + (col * (90 / cols)), y: 5 + (row * (90 / Math.ceil(n / cols))), width: Math.max(15, 85 / cols), height: Math.max(15, 85 / Math.ceil(n / cols)) };
                                            }
                                            return (
                                              <div
                                                key={planeIdx}
                                                style={{
                                                  position: 'absolute',
                                                  left: `${Math.max(0, bounds.x)}%`,
                                                  top: `${Math.max(0, bounds.y)}%`,
                                                  width: `${Math.min(100 - bounds.x, bounds.width)}%`,
                                                  height: `${Math.min(100 - bounds.y, bounds.height)}%`,
                                                  backgroundColor: fillRgba,
                                                  border: `2px solid ${strokeHex}`,
                                                  borderRadius: '4px',
                                                }}
                                              >
                                                <div className="absolute -top-7 left-0 bg-black/90 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1.5 shadow-lg z-10 whitespace-nowrap">
                                                  <span className={`w-3 h-3 rounded shrink-0 ${color === "blue" ? "bg-blue-500" : color === "green" ? "bg-green-500" : color === "purple" ? "bg-purple-500" : color === "orange" ? "bg-orange-500" : color === "red" ? "bg-red-500" : color === "cyan" ? "bg-cyan-500" : "bg-pink-500"}`} />
                                                  <span>{planeLabel}</span>
                                                  <span className="text-[10px] opacity-90">({safeLocale(plane.area)} sq ft)</span>
                                                </div>
                                              </div>
                                            );
                                          })}
                                          {isPrimaryImage && visiblePlanes.some(p => p.polygon && p.polygon.length >= 3) && (
                                            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                                              {visiblePlanes.map((plane, planeIdx) => {
                                                if (!plane.polygon || plane.polygon.length < 3) return null;
                                                const fullPlaneIndex = roofPlanes.findIndex(p => p.name === plane.name);
                                                const color = planeColors[fullPlaneIndex % planeColors.length];
                                                const stroke = color === "blue" ? "#1d4ed8" : color === "green" ? "#15803d" : color === "purple" ? "#7c3aed" : color === "orange" ? "#ea580c" : color === "red" ? "#dc2626" : color === "cyan" ? "#0891b2" : "#be185d";
                                                const points = plane.polygon.map(p => `${Number(p.x)},${Number(p.y)}`).join(' ');
                                                return <polygon key={planeIdx} points={points} fill="none" stroke={stroke} strokeWidth={1.2} />;
                                              })}
                                            </svg>
                                          )}
                                        </div>
                                      )}
                                      {/* Image indicator badges */}
                                      <div className="absolute top-2 right-2 flex gap-1">
                                        {isPrimaryImage && (
                                          <Badge className="bg-blue-600 text-white text-xs">
                                            Top View
                                          </Badge>
                                        )}
                                        {visiblePlanes.length > 0 && (
                                          <Badge variant="outline" className="text-xs bg-black/50 text-white border-white/30">
                                            {visiblePlanes.length} plane{visiblePlanes.length !== 1 ? 's' : ''}
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="absolute bottom-2 left-2 bg-black/70 text-white px-3 py-1 rounded-lg backdrop-blur-sm">
                                        <div className="text-xs font-medium">Image {idx + 1}</div>
                                        {isPrimaryImage && (
                                          <div className="text-xs text-blue-300">Primary (Aerial View)</div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="mt-4 p-3 bg-muted rounded-lg">
                              <p className="text-xs text-muted-foreground">
                                <strong>How it works:</strong> The AI identifies which image provides the best top/aerial view (marked with "Top View" badge). Each roof plane is consistently labeled (A, B, C...) and appears on all images where it's visible. The same physical plane maintains the same letter and color across different views. Colors match the Roof Planes Breakdown cards below.
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Simplified roof plan diagram (no photo).
                          Hide for Google Solar reports where we only have stats, not real geometry. */}
                      {effectiveAnalysis.source !== 'google_solar' && (() => {
                        const sectionsForDiagram = useManualForDiagram && manualPolygons.length > 0
                          ? manualPolygons.map((p, idx) => ({
                              // Approximate a RoofSection-like object from manual polygons
                              id: `manual-${idx}`,
                              name: p.name || `Plane ${idx + 1}`,
                              area: feetPerPct ? polygonAreaPct2(p.points) * feetPerPct * feetPerPct : undefined,
                              polygon: p.points.map(pt => ({ x: pt.x, y: pt.y })),
                            } as any)
                          )
                          : roofPlanes;

                        const hasSections = Array.isArray(sectionsForDiagram) && sectionsForDiagram.length > 0;
                        if (!hasSections) return null;

                        const structuralLinesForDiagram: StructuralLines | undefined =
                          useManualForDiagram
                            ? {
                                hipLines: manualLines.filter(l => l.type === 'hip'),
                                ridgeLines: manualLines.filter(l => l.type === 'ridge'),
                                valleyLines: manualLines.filter(l => l.type === 'valley'),
                                eaveLines: manualLines.filter(l => l.type === 'eave'),
                                rakeLines: manualLines.filter(l => l.type === 'rake'),
                              }
                            : structuralLines;

                        const diagramUrl =
                          typeof document !== 'undefined'
                            ? drawRoofPlanDiagram(sectionsForDiagram, structuralLinesForDiagram, 900, 560)
                            : null;

                        if (!diagramUrl) return null;

                        return (
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2">
                                <Layers className="h-5 w-5" />
                                Roof Plan Diagram
                              </CardTitle>
                              <CardDescription>
                                Top‑down schematic of roof sections with hips, ridges, valleys, eaves, and rakes highlighted.
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="w-full overflow-auto">
                                <img
                                  src={diagramUrl}
                                  alt="Roof plan diagram"
                                  className="mx-auto rounded-lg border bg-white"
                                />
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })()}

                      {/* Roof Planes Breakdown — single source: manual planes OR AI planes */}
                      {((useManualForDiagram && manualPolygons.length > 0) || (!useManualForDiagram && roofPlanes.length > 0)) && (() => {
                        const manualAsPlanes = manualPolygons.map((p) => ({
                          name: p.name,
                          type: 'manual',
                          area: feetPerPct ? polygonAreaPct2(p.points) * feetPerPct * feetPerPct : 0,
                          pitch: 0,
                          pitchRatio: ''
                        }));
                        const displayPlanes = useManualForDiagram ? manualAsPlanes : roofPlanes;
                        return (
                        <Card>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <CardTitle className="flex items-center gap-2">
                                <Layers className="h-5 w-5" />
                                Roof Planes Breakdown
                              </CardTitle>
                              <Badge variant="outline" className="text-sm">
                                {displayPlanes.length} Plane{displayPlanes.length !== 1 ? 's' : ''}
                                {useManualForDiagram ? ' (manual)' : ' (AI)'}
                              </Badge>
                            </div>
                            <CardDescription className="flex items-center gap-4 mt-2">
                              <div className="flex gap-2">
                                <Button
                                  variant={diagramViewMode === "grid" ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setDiagramViewMode("grid")}
                                >
                                  Grid View
                                </Button>
                                <Button
                                  variant={diagramViewMode === "list" ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setDiagramViewMode("list")}
                                >
                                  List View
                                </Button>
                              </div>
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            {diagramViewMode === "grid" ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {displayPlanes.map((plane, idx) => {
                                  const colors = ["blue", "green", "purple", "orange", "red", "cyan", "pink"];
                                  const color = colors[idx % colors.length];
                                  const planeLabel = String.fromCharCode(65 + idx); // A, B, C, etc.
                                  const isManual = plane.type === 'manual';
                                  return (
                                    <div
                                      key={idx}
                                      className={`p-4 rounded-lg border-2 ${
                                        color === "blue" ? "bg-blue-50 dark:bg-blue-950/50 border-blue-200 dark:border-blue-800" :
                                        color === "green" ? "bg-green-50 dark:bg-green-950/50 border-green-200 dark:border-green-800" :
                                        color === "purple" ? "bg-purple-50 dark:bg-purple-950/50 border-purple-200 dark:border-purple-800" :
                                        color === "orange" ? "bg-orange-50 dark:bg-orange-950/50 border-orange-200 dark:border-orange-800" :
                                        color === "red" ? "bg-red-50 dark:bg-red-950/50 border-red-200 dark:border-red-800" :
                                        color === "cyan" ? "bg-cyan-50 dark:bg-cyan-950/50 border-cyan-200 dark:border-cyan-800" :
                                        "bg-pink-50 dark:bg-pink-950/50 border-pink-200 dark:border-pink-800"
                                      }`}
                                    >
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="text-2xl font-bold">{planeLabel}</div>
                                        <Badge variant="outline" className="capitalize">
                                          {isManual ? 'Manual' : (plane.type as string).replace('_', ' ')}
                                        </Badge>
                                      </div>
                                      <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                          <span className="text-muted-foreground">Area:</span>
                                          <span className="font-semibold">{safeLocale(plane.area)} sq ft</span>
                                        </div>
                                        {!isManual && (
                                        <div className="flex justify-between">
                                          <span className="text-muted-foreground">Pitch:</span>
                                          <span className="font-semibold">{plane.pitchRatio || `${safeNum(plane.pitch)}°`}</span>
                                        </div>
                                        )}
                                        {plane.name && (
                                          <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                                            {plane.name}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {displayPlanes.map((plane, idx) => {
                                  const planeLabel = String.fromCharCode(65 + idx); // A, B, C, etc.
                                  const isManual = plane.type === 'manual';
                                  return (
                                    <div key={idx} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50">
                                      <div className="flex items-center gap-4">
                                        <div className="text-2xl font-bold w-12 text-center">{planeLabel}</div>
                                        <div>
                                          <div className="font-medium">{plane.name || `Plane ${planeLabel}`}</div>
                                          <div className="text-sm text-muted-foreground capitalize">
                                            {isManual ? 'Manual' : (plane.type as string).replace('_', ' ')}
                                            {!isManual && ` • Pitch: ${plane.pitchRatio || `${safeNum(plane.pitch)}°`}`}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-lg font-bold">{safeLocale(plane.area)} sq ft</div>
                                        <div className="text-xs text-muted-foreground">{((plane.area || 0) / 100).toFixed(2)} squares</div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                        );
                      })()}

                      {((useManualForDiagram && manualPolygons.length === 0) || (!useManualForDiagram && roofPlanes.length === 0)) && (
                        <Card>
                          <CardContent className="py-12 text-center">
                            <p className="text-muted-foreground">
                              {useManualForDiagram ? 'No roof planes traced yet. Draw planes or use lines only.' : 'No roof planes detected in the analysis'}
                            </p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  );
                })() : (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <p className="text-muted-foreground">Please analyze a roof image first to see the diagram</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Waste Tab */}
              <TabsContent value="waste" className="space-y-6">
                {analysis ? (() => {
                  const effectiveAnalysis = getEffectiveAnalysis() || analysis;
                  const netSqFt = effectiveAnalysis.squareFootage ?? 0;
                  const wasteSqFt = Math.round(netSqFt * (wastePercent / 100));
                  const totalWithWaste = netSqFt + wasteSqFt;
                  const squaresToOrder = (totalWithWaste / 100);
                  const safeNum = (v: unknown, fallback = 0) => (typeof v === 'number' && Number.isFinite(v)) ? v : (Number.isFinite(Number(v)) ? Number(v) : fallback);
                  return (
                    <div className="space-y-6">
                      {/* Prominent waste % control at top — always visible */}
                      <Card className="border-2 border-primary/30 bg-primary/5">
                        <CardContent className="pt-6">
                          <div className="flex flex-wrap items-center gap-4">
                            <span className="text-sm font-medium text-foreground">Waste %</span>
                            <Select
                              value={wastePercent.toString()}
                              onValueChange={(value) => {
                                const pct = Number(value);
                                setWastePercent(pct);
                                if (analysis) calculatePricing(getEffectiveAnalysis() || analysis, pct, regionalLaborRate);
                              }}
                            >
                              <SelectTrigger className="w-[140px] h-10 font-semibold tabular-nums">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {WASTE_OPTIONS.map((pct) => (
                                  <SelectItem key={pct} value={pct.toString()}>{pct}%</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <span className="text-sm text-muted-foreground">Pricing and squares below update when you change this.</span>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Package className="h-5 w-5" />
                            Waste &amp; order quantity
                          </CardTitle>
                          <CardDescription>
                            Set waste % once above; pricing and squares to order recalculate. Same value drives the Estimate tab.
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6 overflow-hidden">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 min-w-0">
                            <div className="p-4 rounded-lg border bg-muted/50 min-w-0 overflow-hidden">
                              <div className="text-xs text-muted-foreground uppercase tracking-wide truncate">Net roof area</div>
                              <div className="text-2xl font-bold mt-1 tabular-nums">{safeNum(netSqFt).toLocaleString()} ft²</div>
                              <p className="text-xs text-muted-foreground mt-1">From {manualOverrides.squareFootage != null ? 'manual' : 'AI'} analysis</p>
                            </div>
                            <div className="p-4 rounded-lg border bg-muted/50 min-w-0">
                              <div className="text-xs text-muted-foreground uppercase tracking-wide truncate mb-2">Waste factor</div>
                              <div className="text-2xl font-bold mt-1 tabular-nums">{wastePercent.toFixed(0)}%</div>
                              <p className="text-xs text-muted-foreground mt-1 truncate">
                                {effectiveAnalysis.complexity === 'complex' ? 'Complex roof' : effectiveAnalysis.complexity === 'moderate' ? 'Moderate' : 'Standard'}
                              </p>
                            </div>
                            <div className="p-4 rounded-lg border bg-muted/50 min-w-0 overflow-hidden">
                              <div className="text-xs text-muted-foreground uppercase tracking-wide truncate">Waste amount</div>
                              <div className="text-2xl font-bold mt-1 tabular-nums">{wasteSqFt.toLocaleString()} ft²</div>
                              <p className="text-xs text-muted-foreground mt-1">Extra for cuts &amp; starter</p>
                            </div>
                            <div className="p-4 rounded-lg border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800 min-w-0 overflow-hidden">
                              <div className="text-xs text-muted-foreground uppercase tracking-wide truncate">Squares to order</div>
                              <div className="text-2xl font-bold mt-1 tabular-nums">{squaresToOrder.toFixed(1)}</div>
                              <p className="text-xs text-muted-foreground mt-1">Net + waste (1 sq = 100 ft²)</p>
                            </div>
                          </div>
                          <div className="rounded-lg border p-4 bg-muted/30 min-w-0">
                            <h4 className="font-semibold mb-2">How waste is applied</h4>
                            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                              <li><strong>Simple roof:</strong> 10–12% — minimal cuts, few hips/valleys.</li>
                              <li><strong>Moderate:</strong> 12–15% — some hips, valleys, or dormers.</li>
                              <li><strong>Complex:</strong> 15–20% — many facets, ridges, and cut-up layout.</li>
                              <li>Waste covers cut-off at ridges/hips, starter course, and misc. waste.</li>
                            </ul>
                            <p className="text-sm text-muted-foreground mt-3">
                              Order squares in whole or half squares when possible (e.g. 21.3 sq → order 21.5 or 22). The amount above is the calculated need including waste; round up if your supplier sells in full squares to avoid shortfalls.
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })() : (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <p className="text-muted-foreground">Please analyze a roof image first</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Pricing Tab - Renamed to Estimate (uses effective analysis so manual overrides apply) */}
              <TabsContent value="pricing" className="space-y-6">
                {Object.keys(pricingTiers).length > 0 && analysis ? (() => {
                  const effectiveAnalysis = getEffectiveAnalysis() || analysis;
                  return (
                  <>
                    {/* Project Overview Cards — all from effective analysis (AI or manual overrides) */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                              <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground">Roofing Squares</div>
                              <div className="text-2xl font-bold">{Math.ceil(effectiveAnalysis.squares)}</div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="text-xs text-muted-foreground mb-2">Material waste %</div>
                          <Select
                            value={wastePercent.toString()}
                            onValueChange={(value) => {
                              const pct = Number(value);
                              setWastePercent(pct);
                              if (analysis) calculatePricing(getEffectiveAnalysis() || analysis, pct, regionalLaborRate);
                            }}
                          >
                            <SelectTrigger className="w-full h-10 font-semibold text-lg">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {WASTE_OPTIONS.map((pct) => (
                                <SelectItem key={pct} value={pct.toString()}>{pct}%</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground mt-1">Added to material cost</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                              <Clock className="h-5 w-5 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground">Est. Duration</div>
                              <div className="text-2xl font-bold">{Math.ceil(effectiveAnalysis.squares * 0.2)} days</div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                              <Hammer className="h-5 w-5 text-red-600 dark:text-red-400" />
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground">Complexity</div>
                              <div className="text-lg font-bold capitalize">{effectiveAnalysis.complexity}</div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Package Options */}
                    <div>
                      <h2 className="text-2xl font-bold mb-4">Package Options</h2>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {['economy', 'standard', 'premium'].map((tier) => {
                          const tierData = pricingTiers[tier];
                          if (!tierData) return null;
                          const isRecommended = tier === 'standard';
                          const isPremium = tier === 'premium';

                          return (
                            <Card key={tier} className={isPremium ? 'border-orange-500' : ''}>
                              {isRecommended && (
                                <div className="bg-blue-600 text-white text-center py-1 text-sm font-medium rounded-t-lg">
                                  Recommended
                                </div>
                              )}
                              <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                  {tier === 'economy' && <Package className="h-5 w-5 text-yellow-500" />}
                                  {tier === 'standard' && <Sparkles className="h-5 w-5 text-blue-500" />}
                                  {tier === 'premium' && <Shield className="h-5 w-5 text-orange-500" />}
                                  {tierData.name} Package
                                </CardTitle>
                                <CardDescription>
                                  {tier === 'economy' && '3-Tab Asphalt Shingles - Budget-friendly option'}
                                  {tier === 'standard' && 'Architectural Shingles - Best value for durability'}
                                  {tier === 'premium' && 'Metal Roofing - Maximum durability and energy efficiency'}
                                </CardDescription>
                              </CardHeader>
                              <CardContent className="space-y-4">
                                <div className="space-y-2">
                                  <div className="flex justify-between text-sm">
                                    <span>Materials:</span>
                                    <span className="font-semibold">
                                      ${tierData.materials.reduce((sum, m) => sum + m.total, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span>Labor:</span>
                                    <span className="font-semibold">
                                      ${tierData.labor.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                </div>
                                <div className="border-t pt-4">
                                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                                    ${tierData.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Shield className="h-4 w-4" />
                                    {tier === 'economy' && '20 year lifespan'}
                                    {tier === 'standard' && '30 year lifespan'}
                                    {tier === 'premium' && '50 year lifespan'}
                                  </div>
                                </div>
                                <Button 
                                  className="w-full" 
                                  variant={isRecommended ? "default" : "outline"}
                                  onClick={() => {
                                    setSelectedTier(tier as any);
                                    saveToEstimates();
                                  }}
                                >
                                  {isRecommended && <CheckCircle className="h-4 w-4 mr-2" />}
                                  Select {tierData.name} Package
                                </Button>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>

                    {/* Detailed Material Breakdown */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Detailed Material Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Tabs value={selectedTier} onValueChange={(v) => setSelectedTier(v as any)}>
                          <TabsList className="grid w-full grid-cols-3 mb-4">
                            <TabsTrigger value="economy">Economy</TabsTrigger>
                            <TabsTrigger value="standard">Standard</TabsTrigger>
                            <TabsTrigger value="premium">Premium</TabsTrigger>
                          </TabsList>

                          {['economy', 'standard', 'premium'].map((tier) => {
                            const tierData = pricingTiers[tier];
                            if (!tierData) return null;

                            return (
                              <TabsContent key={tier} value={tier}>
                                <div className="overflow-x-auto">
                                  <table className="w-full">
                                    <thead>
                                      <tr className="border-b">
                                        <th className="text-left p-2 font-semibold">Item</th>
                                        <th className="text-right p-2 font-semibold">Quantity</th>
                                        <th className="text-right p-2 font-semibold">Unit Cost</th>
                                        <th className="text-right p-2 font-semibold">Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {tierData.materials.map((material, idx) => (
                                        <tr key={idx} className="border-b">
                                          <td className="p-2">{material.name}</td>
                                          <td className="p-2 text-right">{material.quantity} {material.unit}</td>
                                          <td className="p-2 text-right">${material.unitPrice.toFixed(2)}</td>
                                          <td className="p-2 text-right font-semibold">${material.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                        </tr>
                                      ))}
                                      <tr className="bg-muted font-semibold">
                                        <td colSpan={3} className="p-2">Materials Subtotal</td>
                                        <td className="p-2 text-right">
                                          ${tierData.materials.reduce((sum, m) => sum + m.total, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </TabsContent>
                            );
                          })}
                        </Tabs>
                      </CardContent>
                    </Card>

                    {/* How pricing is calculated */}
                    <Card className="bg-muted/50 border-dashed">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <Info className="h-4 w-4" />
                          How material & labor are calculated
                        </CardTitle>
                        <CardDescription className="text-xs">
                          Materials: Quantities come from the roof analysis (squares, linear feet, features). Unit prices use your material list when you have roofing items; otherwise built-in defaults (e.g. shingles, underlayment, flashing, ridge caps, vents, nails). Total materials are adjusted by waste %, pitch, and complexity. Labor: Hours = squares × 2, adjusted by complexity and pitch; cost = hours × your regional labor rate below.
                        </CardDescription>
                      </CardHeader>
                    </Card>

                    {/* Controls */}
                    <div className="flex flex-wrap items-end justify-between gap-4">
                      <div className="flex flex-wrap items-end gap-4">
                        <div>
                          <Label>Regional Labor Rate (per hour)</Label>
                          <Input
                            type="number"
                            value={regionalLaborRate}
                            onChange={(e) => {
                              const newRate = parseFloat(e.target.value);
                              setRegionalLaborRate(newRate);
                              if (analysis) calculatePricing(getEffectiveAnalysis() || analysis, wastePercent, newRate);
                            }}
                            className="w-32 mt-1"
                          />
                        </div>
                      </div>
                      <Button onClick={saveToEstimates} size="lg">
                        <Save className="h-4 w-4 mr-2" />
                        Assign to Estimate
                      </Button>
                      <Button
                        type="button"
                        onClick={openRoofPdfDialog}
                        variant="outline"
                        size="lg"
                      >
                        <FileDown className="h-4 w-4 mr-2" />
                        Save as PDF
                      </Button>
                    </div>
                  </>
                  );
                })() : (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <p className="text-muted-foreground">Complete AI analysis to see pricing estimates</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>

            {/* Estimate Assignment Dialog */}
            <Dialog open={isEstimateDialogOpen} onOpenChange={setIsEstimateDialogOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Assign to Estimate</DialogTitle>
                  <DialogDescription>
                    Select a client to create an estimate from this roof analysis
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Select Client</Label>
                    <Select value={selectedClientId?.toString() || ''} onValueChange={(v) => setSelectedClientId(parseInt(v))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a client" />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        <div className="px-2 py-1.5 sticky top-0 bg-popover z-[101]">
                          <Input
                            placeholder="Search clients..."
                            value={estimateClientSearch}
                            onChange={(e) => setEstimateClientSearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="h-8 text-xs"
                          />
                        </div>
                        {clients
                          .filter((client) => {
                            if (!estimateClientSearch.trim()) return true;
                            const q = estimateClientSearch.toLowerCase();
                            const fullName = `${client.firstName || ''} ${client.lastName || ''}`.toLowerCase();
                            const email = (client.email || '').toLowerCase();
                            return fullName.includes(q) || email.includes(q);
                          })
                          .map((client) => (
                            <SelectItem key={client.id} value={client.id.toString()}>
                              {client.firstName} {client.lastName}
                              {client.email && ` - ${client.email}`}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {analysis && pricingTiers[selectedTier] && (
                    <div className="p-4 bg-muted rounded-lg space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Package:</span>
                        <span className="font-semibold">{pricingTiers[selectedTier].name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Total:</span>
                        <span className="font-bold text-lg">
                          ${pricingTiers[selectedTier].total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsEstimateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={createEstimate} disabled={!selectedClientId || createEstimateMutation.isPending}>
                    {createEstimateMutation.isPending ? 'Creating...' : 'Create Estimate'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* PDF Report Dialog - choose existing client; company from account */}
            <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Download roof measurement PDF</DialogTitle>
                  <DialogDescription>
                    Downloads a roof measurement PDF from this page (not the browser Print dialog). Optionally choose a client
                    for the cover sheet; otherwise we use the property address or &quot;Client&quot;.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="pdf-client">Client</Label>
                    <Select
                      value={selectedClientId ? String(selectedClientId) : ""}
                      onValueChange={(value) => {
                        const id = Number(value);
                        setSelectedClientId(id);
                        const client = clients?.find((c) => c.id === id);
                        if (client) {
                          const fullName = `${client.firstName || ""} ${client.lastName || ""}`.trim();
                          setPdfClientName(fullName);
                        }
                      }}
                    >
                      <SelectTrigger id="pdf-client">
                        <SelectValue placeholder="Select a client" />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1.5 sticky top-0 bg-popover z-[101]">
                          <Input
                            placeholder="Search clients..."
                            value={pdfClientSearch}
                            onChange={(e) => setPdfClientSearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="h-8 text-xs"
                          />
                        </div>
                        {clients
                          ?.filter((client) => {
                            if (!pdfClientSearch.trim()) return true;
                            const q = pdfClientSearch.toLowerCase();
                            const fullName = `${client.firstName || ''} ${client.lastName || ''}`.toLowerCase();
                            const email = (client.email || '').toLowerCase();
                            return fullName.includes(q) || email.includes(q);
                          })
                          .map((client) => (
                            <SelectItem key={client.id} value={String(client.id)}>
                              {`${client.firstName} ${client.lastName}`.trim() || `Client #${client.id}`}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPdfDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      const name =
                        (pdfClientName || '').trim() ||
                        (reportAddress || '').trim() ||
                        'Client';
                      void generatePDFReport(name);
                      setPdfDialogOpen(false);
                    }}
                  >
                    Download PDF
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

          </div>
        </div>
      </div>
    </div>
  );
}

